import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      "nav.home": "Home",
      "nav.jobs": "Jobs",
      "nav.map": "Map",
      "nav.reports": "Reports",
      "nav.settings": "Settings",
      "nav.signIn": "Sign In",
      "nav.signOut": "Logout",
      "nav.getStarted": "Get Started Free",
      "jobs.tab.paused": "Paused",
      "jobs.tab.invoiced": "Invoiced",
      "jobs.tab.archive": "Archive",
      "jobs.searchPlaceholder": "Search jobs by assignee, title, customer…",
      "reports.subtitle2": "Comprehensive insights and data",
      "nav.dashboard": "Dashboard",
      "nav.myDay": "My Day",
      "nav.jobsBoard": "Jobs Board",
      "nav.workplans": "Workplans",
      "nav.taskBoards": "Task Boards",
      "nav.notes": "Notes",
      "nav.staff": "Staff",
      "nav.myTeam": "My Team",
      "nav.teamManagement": "Team Management",
      "nav.timeOff": "Time Off",
      "nav.estimates": "Estimates",
      "nav.customers": "Customers",
      "nav.projects": "Projects",
      "nav.billing": "Billing",
      "nav.auditLog": "Audit Log",
      "nav.archive": "Archive",
      "nav.section.work": "Work",
      "nav.section.people": "People",
      "nav.section.money": "Money",
      "nav.section.admin": "Admin",
      "nav.section.customers": "Customers",
      "nav.section.pinned": "Pinned",
      "nav.approvals": "Approvals",
      "nav.liveMap": "Live Map",
      "nav.payroll": "Payroll",
      "nav.payRuns": "Pay Runs",
      "nav.reimbursements": "Reimbursements",
      "nav.invoices": "Invoices",
      "nav.onClock": "On Clock",
      
      // Landing Page
      "landing.hero.title": "Workforce Time Tracking,",
      "landing.hero.subtitle": "Made Simple",
      "landing.hero.description": "Stop wasting time on timesheets. Track hours, manage jobs, verify locations, and process payroll with one powerful platform.",
      "landing.hero.startTrial": "Start 30-Day Free Trial",
      "landing.hero.watchDemo": "Watch Demo",
      "landing.hero.requestDemo": "Request Demo",
      "landing.hero.noCard": "30 days free, then auto-billed",
      "landing.hero.quickSetup": "Setup in 5 minutes",
      
      // Features
      "features.timeTracking": "Smart Time Tracking",
      "features.timeTracking.desc": "Automated clock-in/out with wellness checks and GPS verification",
      "features.location": "Location Verification",
      "features.location.desc": "Ensure workers are on-site with GPS-based job location tracking",
      "features.team": "Team Management",
      "features.team.desc": "Organize teams, assign supervisors, and manage your entire workforce",
      "features.reports": "Advanced Reports",
      "features.reports.desc": "Payroll reports, activity analytics, and CSV exports for accounting",
      "features.jobs": "Job Management",
      "features.jobs.desc": "Assign jobs, track progress, and require photo documentation",
      "features.security": "Secure & Compliant",
      "features.security.desc": "Enterprise-grade security with full audit trails and data protection",
      
      // Auth
      "auth.welcomeBack": "Welcome Back",
      "auth.signInToTrack": "Sign in to track your time",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.signIn": "Sign In",
      "auth.forgotPassword": "Forgot Password?",
      "auth.newToHinesTime": "New to Hines Time?",
      "auth.registerCompany": "Register Your Company",
      
      // Home/Dashboard
      "home.goodMorning": "Good Morning",
      "home.goodAfternoon": "Good Afternoon",
      "home.goodEvening": "Good Evening",
      "home.clockIn": "Clock In",
      "home.clockOut": "Clock Out",
      "home.clockedIn": "CLOCKED IN",
      "home.readyToClock": "READY TO CLOCK IN",
      "home.todayHours": "Today's Hours",
      "home.weekHours": "Week's Hours",
      "home.recentActivity": "Recent Activity",
      "home.yourJobs": "Your Jobs",
      
      // Supervisor Team Management
      "supervisor.teamManagement": "Team Management",
      "supervisor.clockInTeam": "Clock In Team",
      "supervisor.clockOutTeam": "Clock Out Team",
      "supervisor.teamDescription": "As a supervisor, you can clock in or clock out your entire team with one action.",
      "supervisor.clockInResults": "Team Clock In Results:",
      "supervisor.clockOutResults": "Team Clock Out Results:",
      "supervisor.clockedIn": "Clocked In",
      "supervisor.clockedOut": "Clocked Out",
      "supervisor.alreadyIn": "Already In",
      "supervisor.notClockedIn": "Not Clocked In",
      "supervisor.totalMembers": "Total Members",
      
      // Wellness Check
      "wellness.title": "Wellness Check Required",
      "wellness.subtitle": "Safety First!",
      "wellness.message": "Before clocking in, please confirm that you are physically and mentally ready for work.",
      "wellness.confirm": "I confirm that I am fit for duty and ready to work safely",
      "wellness.confirmButton": "✓ Confirm & Clock In",
      "wellness.cancel": "Cancel",
      
      // Toolbox
      "toolbox.title": "Toolbox Meeting",
      "toolbox.readTopic": "Please read today's safety topic before starting work",
      "toolbox.openPdf": "Open PDF Safety Document",
      "toolbox.iReadThis": "I Read This - Continue Clock In",
      "toolbox.goBack": "Go Back",
      "toolbox.question": "Quick Safety Check",
      "toolbox.didYouRead": "Did you read today's toolbox topic?",
      "toolbox.yesIRead": "Yes, I Read It",
      "toolbox.noIDidnt": "No, I Didn't",
      "toolbox.compliance": "Toolbox Meeting Compliance",
      
      // Jobs - Three-Tab System
      "jobs.title": "Jobs",
      "jobs.addJob": "Add Job",
      "jobs.startJob": "Start Job",
      "jobs.completeJob": "Complete Job",
      "jobs.addPhotos": "Add Photos",
      "jobs.viewPhotos": "View Photos",
      "jobs.photosRequired": "Photos Required",
      "jobs.assignedTo": "Assigned to",
      "jobs.customer": "Customer",
      "jobs.location": "Location",
      "jobs.jobTitle": "Job Title",
      "jobs.jobDescription": "Job Description",
      "jobs.enterTitle": "Enter job title",
      "jobs.enterDescription": "Enter job description",
      "jobs.selectUser": "Select user",
      "jobs.customerName": "Customer name (optional)",
      "jobs.customerEmail": "Customer email (optional)",
      "jobs.streetAddress": "Street Address",
      "jobs.city": "City",
      "jobs.state": "State/Province",
      "jobs.postalCode": "Postal Code",
      "jobs.country": "Country",
      "jobs.selectCountry": "Select country",
      "jobs.selectState": "Select state",
      "jobs.gpsCoordinates": "GPS Coordinates (Optional)",
      "jobs.latitude": "Latitude",
      "jobs.longitude": "Longitude",
      "jobs.requirePhotos": "Require job completion photos",
      "jobs.requireLocation": "Require location verification",
      "jobs.locationHelp": "Worker must be within 500m of job site to start work",
      "jobs.noLocationHelp": "Worker can start job from anywhere",
      "jobs.createJob": "Create Job",
      "jobs.cancel": "Cancel",
      "jobs.editJob": "Edit Job",
      "jobs.deleteJob": "Delete Job",
      "jobs.reassignJob": "Reassign Job",
      "jobs.sendReminder": "Send Reminder",
      "jobs.acceptJob": "Accept Job",
      "jobs.rejectJob": "Reject Job",
      "jobs.viewOnMap": "View on Map",
      "jobs.uploadPhotos": "Upload Photos",
      "jobs.emailPhotos": "Email Photos",
      "jobs.emailReport": "Email Report",
      "jobs.viewStats": "View Stats",
      "jobs.tab.todo": "To Do",
      "jobs.tab.inProgress": "In Progress",
      "jobs.tab.completed": "Completed",
      "jobs.status.pending": "Pending",
      "jobs.status.accepted": "Accepted",
      "jobs.status.rejected": "Rejected",
      "jobs.status.inProgress": "In Progress",
      "jobs.status.completed": "Completed",
      "jobs.overdue": "OVERDUE",
      "jobs.assignedAgo": "Assigned {{time}} ago",
      "jobs.duration": "Duration: {{duration}}",
      "jobs.completedBy": "Completed by {{name}}",
      "jobs.startedAt": "Started at {{time}}",
      "jobs.completedAt": "Completed at {{time}}",
      "jobs.photos": "{{count}} photos",
      "jobs.noPhotos": "No photos",
      "jobs.photosCount": "{{count}}/{{total}} photos",
      "jobs.morePhotos": "+{{count}} more",
      "jobs.confirmDelete": "Are you sure you want to delete this job?",
      "jobs.confirmReject": "Are you sure you want to reject this job?",
      "jobs.rejectReason": "Reason for rejection (optional)",
      "jobs.completionNotes": "Completion Notes",
      "jobs.enterNotes": "Enter completion notes...",
      "jobs.loading": "Loading jobs...",
      "jobs.noJobs": "No jobs found",
      "jobs.noTodoJobs": "No pending jobs",
      "jobs.noInProgressJobs": "No jobs in progress",
      "jobs.noCompletedJobs": "No completed jobs",
      
      // Reports
      "reports.title": "Reports & Analytics",
      "reports.subtitle": "Comprehensive insights and data",
      "reports.supervisorView": "Supervisor View - Team-Specific Reports",
      "reports.supervisorDescription": "You are viewing reports filtered for your team members only. The data shown below includes only activities, hours, and jobs related to your assigned team.",
      "reports.yourTeam": "Your Team:",
      "reports.noTeamAssigned": "You are not currently assigned to a team. Please contact your administrator.",
      "reports.refresh": "Refresh",
      "reports.generateReports": "Generate Reports",
      "reports.startDate": "Start Date",
      "reports.endDate": "End Date",
      "reports.payroll": "Payroll Report",
      "reports.userActivity": "User Activity",
      "reports.jobStats": "Job Statistics",
      "reports.teamPerformance": "Team Performance",
      "reports.timeEditor": "Time Editor",
      "reports.toolboxCompliance": "Toolbox Compliance",
      "reports.deletedEntries": "Deleted Entries",
      "reports.totalHours": "Total Hours",
      "reports.totalEntries": "Total Entries",
      "reports.activeUsers": "Active Users",
      "reports.exportCSV": "Export to CSV",
      "reports.noData": "No data available for this period",
      "reports.userName": "User Name",
      "reports.hours": "Hours",
      "reports.clockIns": "Clock Ins",
      "reports.avgHoursPerDay": "Avg Hours/Day",
      "reports.jobsCompleted": "Jobs Completed",
      "reports.teamName": "Team Name",
      "reports.memberCount": "Members",
      "reports.avgHoursPerMember": "Avg Hours/Member",
      "reports.completionRate": "Completion Rate",
      "reports.statusBreakdown": "Status Breakdown",
      "reports.complianceRate": "Compliance Rate",
      "reports.topicDate": "Topic Date",
      "reports.topicTitle": "Topic Title",
      "reports.acknowledged": "Acknowledged",
      "reports.notAcknowledged": "Not Acknowledged",
      "reports.editEntry": "Edit Entry",
      "reports.deleteEntry": "Delete Entry",
      "reports.date": "Date",
      "reports.clockIn": "Clock In",
      "reports.clockOut": "Clock Out",
      "reports.editedBy": "Edited By",
      "reports.editedAt": "Edited At",
      "reports.reason": "Reason",
      "reports.originalTime": "Original Time",
      "reports.newTime": "New Time",
      "reports.auditTrail": "Audit Trail",
      
      // Settings
      "settings.title": "Settings",
      "settings.profile": "Profile Information",
      "settings.editProfile": "Edit Profile",
      "settings.changePassword": "Change Password",
      "settings.currentPassword": "Current Password",
      "settings.newPassword": "New Password",
      "settings.confirmPassword": "Confirm Password",
      "settings.management": "Management",
      "settings.manageUsers": "Manage Users",
      "settings.manageTeams": "Manage Teams",
      "settings.toolboxTopics": "Toolbox Meeting Topics",
      "settings.billing": "Billing & Subscription",
      "settings.companyBranding": "Company Branding",
      "settings.twoFactor": "Two-Factor Authentication",
      "settings.userManual": "User Manual",
      "settings.termsOfService": "Terms of Service",
      "settings.privacyPolicy": "Privacy Policy",
      "settings.firstName": "First Name",
      "settings.lastName": "Last Name",
      "settings.email": "Email",
      "settings.phone": "Phone",
      "settings.address": "Address",
      "settings.role": "Role",
      "settings.updateProfile": "Update Profile",
      "settings.uploadLogo": "Upload Logo",
      "settings.companyName": "Company Name",
      "settings.companyInfo": "Company Information",
      "settings.notifications": "Email Notifications",
      "settings.notifyOnJobComplete": "Notify admins when jobs are completed",
      "settings.notifyCustomer": "Automatically email customers on job completion",
      
      // Users Management
      "users.title": "Manage Users",
      "users.addUser": "Add User",
      "users.editUser": "Edit User",
      "users.deleteUser": "Delete User",
      "users.resendInvitation": "Resend Invitation",
      "users.firstName": "First Name",
      "users.lastName": "Last Name",
      "users.email": "Email Address",
      "users.phone": "Phone Number",
      "users.role": "Role",
      "users.userType": "User Type",
      "users.selectRole": "Select role",
      "users.selectType": "Select type",
      "users.role.admin": "Administrator",
      "users.role.supervisor": "Supervisor",
      "users.role.user": "User",
      "users.type.employee": "Employee",
      "users.type.contractor": "Contractor",
      "users.status": "Status",
      "users.active": "Active",
      "users.inactive": "Inactive",
      "users.confirmDelete": "Are you sure you want to delete this user?",
      "users.invitationSent": "Invitation email sent",
      "users.userCreated": "User created successfully",
      "users.userUpdated": "User updated successfully",
      "users.userDeleted": "User deleted successfully",
      
      // Teams Management
      "teams.title": "Manage Teams",
      "teams.addTeam": "Add Team",
      "teams.editTeam": "Edit Team",
      "teams.deleteTeam": "Delete Team",
      "teams.teamName": "Team Name",
      "teams.teamDescription": "Team Description",
      "teams.supervisor": "Supervisor",
      "teams.selectSupervisor": "Select supervisor",
      "teams.members": "Team Members",
      "teams.addMembers": "Add Members",
      "teams.removeMembers": "Remove Members",
      "teams.memberCount": "{{count}} members",
      "teams.noMembers": "No members assigned",
      "teams.teamCreated": "Team created successfully",
      "teams.teamUpdated": "Team updated successfully",
      "teams.teamDeleted": "Team deleted successfully",
      "teams.confirmDelete": "Are you sure you want to delete this team?",
      
      // Billing & Payments
      "billing.title": "Billing & Subscription",
      "billing.subscription": "Subscription Details",
      "billing.status": "Status",
      "billing.plan": "Plan",
      "billing.activeUsers": "Active Users",
      "billing.monthlyCost": "Monthly Cost",
      "billing.nextBilling": "Next Billing Date",
      "billing.setupFee": "Setup Fee",
      "billing.setupFeePaid": "Paid",
      "billing.setupFeePending": "Payment Required",
      "billing.paymentMethod": "Payment Method",
      "billing.updatePayment": "Update Payment Method",
      "billing.viewInvoices": "View Invoices",
      "billing.cancelSubscription": "Cancel Subscription",
      "billing.trialEnds": "Trial ends on {{date}}",
      "billing.perUserPerMonth": "per user/month",
      "billing.currency": "Currency",
      "billing.setupPayment": "Setup Payment",
      
      // Two-Factor Authentication
      "2fa.title": "Two-Factor Authentication",
      "2fa.enable": "Enable 2FA",
      "2fa.disable": "Disable 2FA",
      "2fa.scanQR": "Scan QR Code",
      "2fa.enterCode": "Enter verification code",
      "2fa.backupCodes": "Backup Codes",
      "2fa.saveBackupCodes": "Save these backup codes in a safe place",
      "2fa.verificationCode": "6-digit code",
      "2fa.verify": "Verify & Enable",
      "2fa.enabled": "2FA is enabled",
      "2fa.disabled": "2FA is disabled",
      "2fa.required": "2FA is required by your company",
      "2fa.optional": "2FA is optional",
      
      // Common
      "common.save": "Save",
      "common.cancel": "Cancel",
      "common.delete": "Delete",
      "common.edit": "Edit",
      "common.close": "Close",
      "common.submit": "Submit",
      "common.loading": "Loading...",
      "common.success": "Success",
      "common.error": "Error",
      "common.confirm": "Confirm",
      "common.yes": "Yes",
      "common.no": "No",
      
      // Messages
      "message.success.clockIn": "Clocked in successfully!",
      "message.success.clockOut": "Clocked out successfully!",
      "message.error.clockIn": "Unable to clock in. Please try again.",
      "message.error.clockOut": "Unable to clock out. Please try again.",
      "message.success.jobCreated": "Job created successfully!",
      "message.success.jobUpdated": "Job updated successfully!",
      "message.success.jobDeleted": "Job deleted successfully!",
      "message.success.jobStarted": "Job started successfully!",
      "message.success.jobCompleted": "Job completed successfully!",
      "message.success.photoUploaded": "Photo uploaded successfully!",
      "message.success.photoDeleted": "Photo deleted successfully!",
      "message.success.profileUpdated": "Profile updated successfully!",
      "message.success.passwordChanged": "Password changed successfully!",
      "message.success.settingsSaved": "Settings saved successfully!",
      "message.error.uploadFailed": "Upload failed. Please try again.",
      "message.error.locationRequired": "Location access is required for this feature.",
      "message.error.tooFarFromJob": "You are too far from the job location.",
      "message.error.photosRequired": "Please upload required photos before completing.",
      "message.error.invalidInput": "Please check your input and try again.",
      "message.error.serverError": "Server error. Please try again later.",
      "message.error.unauthorized": "You don't have permission to perform this action.",
      "message.warning.unsavedChanges": "You have unsaved changes.",
      "message.confirm.delete": "This action cannot be undone.",
      
      // Auth & Password
      "auth.welcomeBack": "Welcome Back",
      "auth.signInToTrack": "Sign in to track your time",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.signIn": "Sign In",
      "auth.forgotPassword": "Forgot Password?",
      "auth.newToHinesTime": "New to Hines Time?",
      "auth.registerCompany": "Register Your Company",
      "auth.enterEmail": "Enter your email",
      "auth.enterPassword": "Enter your password",
      "auth.resetPassword": "Reset Password",
      "auth.sendResetLink": "Send Reset Link",
      "auth.backToSignIn": "Back to Sign In",
      "auth.resetLinkSent": "Password reset link sent to your email",
      "auth.createNewPassword": "Create New Password",
      "auth.passwordRequirements": "Password must be at least 8 characters",
      "auth.passwordMismatch": "Passwords do not match",
      "auth.invalidCredentials": "Invalid email or password",
      "auth.accountLocked": "Account is locked. Contact administrator.",
      "auth.sessionExpired": "Session expired. Please sign in again.",
      "auth.firstLoginRequired": "Please change your password on first login",
      "auth.selectOrganization": "Select Organization",
      "auth.multipleOrganizations": "Your email is registered with multiple organizations",
      
      // Map
      "map.title": "Team & Jobs Map",
      "map.activeUsers": "Active Users",
      "map.jobLocations": "Job Locations",
      "map.noActiveUsers": "No users currently clocked in",
      "map.noJobs": "No jobs with locations",
      "map.clockedInAt": "Clocked in at {{time}}",
      "map.lastUpdated": "Last updated: {{time}}",
      "map.refreshMap": "Refresh Map",
      "map.centerOnUser": "Center on my location",
      "map.showAllUsers": "Show all users",
      "map.showAllJobs": "Show all jobs",
      "map.filterByTeam": "Filter by team",
      
      // Company Registration
      "register.title": "Register Your Company",
      "register.companyDetails": "Company Details",
      "register.adminDetails": "Administrator Details",
      "register.companyName": "Company Name",
      "register.streetAddress": "Street Address",
      "register.city": "City",
      "register.state": "State/Province",
      "register.postalCode": "Postal Code",
      "register.country": "Country",
      "register.adminFirstName": "Admin First Name",
      "register.adminLastName": "Admin Last Name",
      "register.adminEmail": "Admin Email",
      "register.adminPhone": "Admin Phone",
      "register.adminPassword": "Admin Password",
      "register.confirmPassword": "Confirm Password",
      "register.agreeToTerms": "I agree to the Terms of Service and Privacy Policy",
      "register.createAccount": "Create Account",
      "register.alreadyHaveAccount": "Already have an account?",
      "register.signInHere": "Sign in here",
      "register.companyRegistered": "Company registered successfully!",
      
      // Time Editor
      "timeEditor.title": "Time Entry Editor",
      "timeEditor.selectUser": "Select user to edit",
      "timeEditor.selectDate": "Select date range",
      "timeEditor.fetchEntries": "Fetch Entries",
      "timeEditor.noEntries": "No entries found for this period",
      "timeEditor.editEntry": "Edit Entry",
      "timeEditor.bulkEdit": "Bulk Edit",
      "timeEditor.deleteEntry": "Delete Entry",
      "timeEditor.editReason": "Edit Reason",
      "timeEditor.deleteReason": "Delete Reason",
      "timeEditor.reasonRequired": "Reason is required",
      "timeEditor.clockInTime": "Clock In Time",
      "timeEditor.clockOutTime": "Clock Out Time",
      "timeEditor.originalHours": "Original: {{hours}}",
      "timeEditor.newHours": "New: {{hours}}",
      "timeEditor.auditLog": "Audit Log",
      "timeEditor.editedBy": "Edited by {{name}} on {{date}}",
      "timeEditor.deletedBy": "Deleted by {{name}} on {{date}}",
      "timeEditor.applyShift": "Apply Shift Duration",
      "timeEditor.shiftDuration": "Shift Duration (hours)",
      "timeEditor.applyToAll": "Apply to all selected entries",
    }
  },
  fr: {
    translation: {
      // Navigation
      "nav.home": "Accueil",
      "nav.jobs": "Emplois",
      "nav.map": "Carte",
      "nav.reports": "Rapports",
      "nav.settings": "Paramètres",
      "nav.signIn": "Se connecter",
      "nav.signOut": "Déconnexion",
      "nav.getStarted": "Commencer Gratuitement",
      "jobs.tab.paused": "En pause",
      "jobs.tab.invoiced": "Facturé",
      "jobs.tab.archive": "Archives",
      "jobs.searchPlaceholder": "Rechercher par assigné, titre, client…",
      "reports.subtitle2": "Analyses et données complètes",
      "nav.dashboard": "Tableau de bord",
      "nav.myDay": "Ma journée",
      "nav.jobsBoard": "Tableau des travaux",
      "nav.workplans": "Plans de travail",
      "nav.taskBoards": "Tableaux de tâches",
      "nav.notes": "Notes",
      "nav.staff": "Personnel",
      "nav.myTeam": "Mon équipe",
      "nav.teamManagement": "Gestion d'équipe",
      "nav.timeOff": "Congés",
      "nav.estimates": "Devis",
      "nav.customers": "Clients",
      "nav.projects": "Projets",
      "nav.billing": "Facturation",
      "nav.auditLog": "Journal d'audit",
      "nav.archive": "Archives",
      "nav.section.work": "Travail",
      "nav.section.people": "Personnes",
      "nav.section.money": "Finances",
      "nav.section.admin": "Admin",
      "nav.section.customers": "Clients",
      "nav.section.pinned": "Épinglés",
      "nav.approvals": "Approbations",
      "nav.liveMap": "Carte en direct",
      "nav.payroll": "Paie",
      "nav.payRuns": "Cycles de paie",
      "nav.reimbursements": "Remboursements",
      "nav.invoices": "Factures",
      "nav.onClock": "Au travail",
      
      // Landing Page
      "landing.hero.title": "Suivi du Temps de Travail,",
      "landing.hero.subtitle": "Simplifié",
      "landing.hero.description": "Arrêtez de perdre du temps sur les feuilles de temps. Suivez les heures, gérez les emplois, vérifiez les emplacements et traitez la paie avec une seule plateforme puissante.",
      "landing.hero.startTrial": "Commencer l'Essai Gratuit de 30 Jours",
      "landing.hero.watchDemo": "Voir la Démo",
      "landing.hero.requestDemo": "Demander une Démo",
      "landing.hero.noCard": "30 jours gratuits, puis facturé automatiquement",
      "landing.hero.quickSetup": "Configuration en 5 minutes",
      
      // Features
      "features.timeTracking": "Suivi du Temps Intelligent",
      "features.timeTracking.desc": "Pointage automatique avec vérifications de bien-être et GPS",
      "features.location": "Vérification de Localisation",
      "features.location.desc": "Assurez-vous que les travailleurs sont sur place avec le suivi GPS",
      "features.team": "Gestion d'Équipe",
      "features.team.desc": "Organisez les équipes, assignez les superviseurs et gérez tout votre personnel",
      "features.reports": "Rapports Avancés",
      "features.reports.desc": "Rapports de paie, analyses d'activité et exports CSV",
      "features.jobs": "Gestion des Emplois",
      "features.jobs.desc": "Assignez des emplois, suivez les progrès et exigez des photos",
      "features.security": "Sécurisé et Conforme",
      "features.security.desc": "Sécurité de niveau entreprise avec pistes d'audit complètes",
      
      // Auth
      "auth.welcomeBack": "Bon Retour",
      "auth.signInToTrack": "Connectez-vous pour suivre votre temps",
      "auth.email": "Courriel",
      "auth.password": "Mot de passe",
      "auth.signIn": "Se Connecter",
      "auth.forgotPassword": "Mot de passe oublié?",
      "auth.newToHinesTime": "Nouveau sur Hines Time?",
      "auth.registerCompany": "Enregistrez Votre Entreprise",
      
      // Home/Dashboard
      "home.goodMorning": "Bonjour",
      "home.goodAfternoon": "Bon Après-midi",
      "home.goodEvening": "Bonsoir",
      "home.clockIn": "Pointer",
      "home.clockOut": "Dépointer",
      "home.clockedIn": "POINTÉ",
      "home.readyToClock": "PRÊT À POINTER",
      "home.todayHours": "Heures d'Aujourd'hui",
      "home.weekHours": "Heures de la Semaine",
      "home.recentActivity": "Activité Récente",
      "home.yourJobs": "Vos Emplois",
      
      // Supervisor Team Management
      "supervisor.teamManagement": "Gestion d'Équipe",
      "supervisor.clockInTeam": "Pointer l'Équipe",
      "supervisor.clockOutTeam": "Dépointer l'Équipe",
      "supervisor.teamDescription": "En tant que superviseur, vous pouvez pointer ou dépointer toute votre équipe en une seule action.",
      "supervisor.clockInResults": "Résultats du Pointage d'Équipe:",
      "supervisor.clockOutResults": "Résultats du Dépointage d'Équipe:",
      "supervisor.clockedIn": "Pointés",
      "supervisor.clockedOut": "Dépointés",
      "supervisor.alreadyIn": "Déjà Pointés",
      "supervisor.notClockedIn": "Non Pointés",
      "supervisor.totalMembers": "Membres Totaux",
      
      // Wellness
      "wellness.title": "Vérification de Bien-être Requise",
      "wellness.subtitle": "La Sécurité d'Abord!",
      "wellness.message": "Avant de pointer, veuillez confirmer que vous êtes physiquement et mentalement prêt pour le travail.",
      "wellness.confirm": "Je confirme que je suis apte au travail et prêt à travailler en toute sécurité",
      "wellness.confirmButton": "✓ Confirmer et Pointer",
      "wellness.cancel": "Annuler",
      
      // Toolbox
      "toolbox.title": "Réunion Boîte à Outils",
      "toolbox.readTopic": "Veuillez lire le sujet de sécurité d'aujourd'hui avant de commencer le travail",
      "toolbox.openPdf": "Ouvrir le Document PDF de Sécurité",
      "toolbox.iReadThis": "J'ai Lu Ceci - Continuer à Pointer",
      "toolbox.goBack": "Retour",
      "toolbox.question": "Vérification Rapide de Sécurité",
      "toolbox.didYouRead": "Avez-vous lu le sujet de la boîte à outils d'aujourd'hui?",
      "toolbox.yesIRead": "Oui, Je l'ai Lu",
      "toolbox.noIDidnt": "Non, Je ne l'ai Pas Fait",
      "toolbox.compliance": "Conformité des Réunions Boîte à Outils",
      
      // Jobs - Three-Tab System
      "jobs.title": "Emplois",
      "jobs.addJob": "Ajouter un Emploi",
      "jobs.startJob": "Commencer l'Emploi",
      "jobs.completeJob": "Terminer l'Emploi",
      "jobs.addPhotos": "Ajouter des Photos",
      "jobs.viewPhotos": "Voir les Photos",
      "jobs.photosRequired": "Photos Requises",
      "jobs.assignedTo": "Assigné à",
      "jobs.customer": "Client",
      "jobs.location": "Emplacement",
      "jobs.jobTitle": "Titre de l'Emploi",
      "jobs.jobDescription": "Description de l'Emploi",
      "jobs.enterTitle": "Entrez le titre de l'emploi",
      "jobs.enterDescription": "Entrez la description de l'emploi",
      "jobs.selectUser": "Sélectionner un utilisateur",
      "jobs.customerName": "Nom du client (optionnel)",
      "jobs.customerEmail": "Email du client (optionnel)",
      "jobs.streetAddress": "Adresse",
      "jobs.city": "Ville",
      "jobs.state": "Province/État",
      "jobs.postalCode": "Code Postal",
      "jobs.country": "Pays",
      "jobs.selectCountry": "Sélectionner le pays",
      "jobs.selectState": "Sélectionner la province",
      "jobs.createJob": "Créer l'Emploi",
      "jobs.cancel": "Annuler",
      "jobs.editJob": "Modifier l'Emploi",
      "jobs.deleteJob": "Supprimer l'Emploi",
      "jobs.reassignJob": "Réassigner l'Emploi",
      "jobs.sendReminder": "Envoyer un Rappel",
      "jobs.acceptJob": "Accepter l'Emploi",
      "jobs.rejectJob": "Rejeter l'Emploi",
      "jobs.viewOnMap": "Voir sur la Carte",
      "jobs.uploadPhotos": "Télécharger des Photos",
      "jobs.emailPhotos": "Envoyer Photos par Email",
      "jobs.emailReport": "Envoyer Rapport par Email",
      "jobs.viewStats": "Voir les Statistiques",
      "jobs.tab.todo": "À Faire",
      "jobs.tab.inProgress": "En Cours",
      "jobs.tab.completed": "Terminés",
      "jobs.status.pending": "En Attente",
      "jobs.status.accepted": "Accepté",
      "jobs.status.rejected": "Rejeté",
      "jobs.status.inProgress": "En Cours",
      "jobs.status.completed": "Terminé",
      "jobs.overdue": "EN RETARD",
      "jobs.duration": "Durée: {{duration}}",
      "jobs.completedBy": "Terminé par {{name}}",
      "jobs.noJobs": "Aucun emploi trouvé",
      "jobs.noTodoJobs": "Aucun emploi en attente",
      "jobs.noInProgressJobs": "Aucun emploi en cours",
      "jobs.noCompletedJobs": "Aucun emploi terminé",
      
      // Reports
      "reports.title": "Rapports et Analyses",
      "reports.subtitle": "Informations et données complètes",
      "reports.supervisorView": "Vue Superviseur - Rapports Spécifiques à l'Équipe",
      "reports.supervisorDescription": "Vous consultez des rapports filtrés uniquement pour les membres de votre équipe. Les données affichées ci-dessous incluent uniquement les activités, heures et emplois liés à votre équipe assignée.",
      "reports.yourTeam": "Votre Équipe:",
      "reports.noTeamAssigned": "Vous n'êtes actuellement affecté à aucune équipe. Veuillez contacter votre administrateur.",
      "reports.refresh": "Actualiser",
      "reports.generateReports": "Générer les Rapports",
      "reports.startDate": "Date de Début",
      "reports.endDate": "Date de Fin",
      
      // Settings
      "settings.title": "Paramètres",
      "settings.profile": "Informations de Profil",
      "settings.editProfile": "Modifier le Profil",
      "settings.changePassword": "Changer le Mot de Passe",
      "settings.currentPassword": "Mot de Passe Actuel",
      "settings.newPassword": "Nouveau Mot de Passe",
      "settings.confirmPassword": "Confirmer le Mot de Passe",
      "settings.management": "Gestion",
      "settings.manageUsers": "Gérer les Utilisateurs",
      "settings.manageTeams": "Gérer les Équipes",
      "settings.toolboxTopics": "Sujets de Réunion Boîte à Outils",
      "settings.billing": "Facturation et Abonnement",
      "settings.companyBranding": "Image de Marque de l'Entreprise",
      "settings.twoFactor": "Authentification à Deux Facteurs",
      "settings.userManual": "Manuel d'Utilisateur",
      "settings.termsOfService": "Conditions d'Utilisation",
      "settings.privacyPolicy": "Politique de Confidentialité",
      
      // Users Management
      "users.title": "Gérer les Utilisateurs",
      "users.addUser": "Ajouter un Utilisateur",
      "users.editUser": "Modifier l'Utilisateur",
      "users.deleteUser": "Supprimer l'Utilisateur",
      "users.resendInvitation": "Renvoyer l'Invitation",
      "users.firstName": "Prénom",
      "users.lastName": "Nom",
      "users.email": "Adresse Email",
      "users.phone": "Numéro de Téléphone",
      "users.role": "Rôle",
      "users.userType": "Type d'Utilisateur",
      "users.role.admin": "Administrateur",
      "users.role.supervisor": "Superviseur",
      "users.role.user": "Utilisateur",
      "users.type.employee": "Employé",
      "users.type.contractor": "Entrepreneur",
      "users.confirmDelete": "Êtes-vous sûr de vouloir supprimer cet utilisateur?",
      
      // Teams Management
      "teams.title": "Gérer les Équipes",
      "teams.addTeam": "Ajouter une Équipe",
      "teams.editTeam": "Modifier l'Équipe",
      "teams.deleteTeam": "Supprimer l'Équipe",
      "teams.teamName": "Nom de l'Équipe",
      "teams.supervisor": "Superviseur",
      "teams.members": "Membres de l'Équipe",
      "teams.confirmDelete": "Êtes-vous sûr de vouloir supprimer cette équipe?",
      
      // Common
      "common.save": "Enregistrer",
      "common.cancel": "Annuler",
      "common.delete": "Supprimer",
      "common.edit": "Modifier",
      "common.close": "Fermer",
      "common.submit": "Soumettre",
      "common.loading": "Chargement...",
      "common.success": "Succès",
      "common.error": "Erreur",
      "common.confirm": "Confirmer",
      "common.yes": "Oui",
      "common.no": "Non",
      
      // Messages
      "message.success.clockIn": "Pointé avec succès!",
      "message.success.clockOut": "Dépointé avec succès!",
      "message.error.clockIn": "Impossible de pointer. Veuillez réessayer.",
      "message.error.clockOut": "Impossible de dépointer. Veuillez réessayer.",
      "message.success.jobCreated": "Emploi créé avec succès!",
      "message.success.jobUpdated": "Emploi mis à jour avec succès!",
      "message.success.jobDeleted": "Emploi supprimé avec succès!",
      "message.success.jobStarted": "Emploi commencé avec succès!",
      "message.success.jobCompleted": "Emploi terminé avec succès!",
      "message.success.photoUploaded": "Photo téléchargée avec succès!",
      "message.success.photoDeleted": "Photo supprimée avec succès!",
      "message.success.profileUpdated": "Profil mis à jour avec succès!",
      "message.success.passwordChanged": "Mot de passe changé avec succès!",
      "message.success.settingsSaved": "Paramètres enregistrés avec succès!",
      "message.error.uploadFailed": "Échec du téléchargement. Veuillez réessayer.",
      "message.error.locationRequired": "L'accès à la localisation est requis pour cette fonctionnalité.",
      "message.error.tooFarFromJob": "Vous êtes trop loin de l'emplacement du travail.",
      "message.error.photosRequired": "Veuillez télécharger les photos requises avant de terminer.",
      "message.error.invalidInput": "Veuillez vérifier votre saisie et réessayer.",
      "message.error.serverError": "Erreur du serveur. Veuillez réessayer plus tard.",
      "message.error.unauthorized": "Vous n'avez pas la permission d'effectuer cette action.",
      "message.warning.unsavedChanges": "Vous avez des modifications non enregistrées.",
      "message.confirm.delete": "Cette action ne peut pas être annulée.",
      
      // Jobs - Additional keys
      "jobs.gpsCoordinates": "Coordonnées GPS (Optionnel)",
      "jobs.latitude": "Latitude",
      "jobs.longitude": "Longitude",
      "jobs.requirePhotos": "Exiger des photos de fin d'emploi",
      "jobs.requireLocation": "Exiger une vérification de localisation",
      "jobs.locationHelp": "Le travailleur doit être à moins de 500m du site pour commencer",
      "jobs.noLocationHelp": "Le travailleur peut commencer le travail de n'importe où",
      "jobs.assignedAgo": "Assigné il y a {{time}}",
      "jobs.startedAt": "Commencé à {{time}}",
      "jobs.completedAt": "Terminé à {{time}}",
      "jobs.photos": "{{count}} photos",
      "jobs.noPhotos": "Pas de photos",
      "jobs.photosCount": "{{count}}/{{total}} photos",
      "jobs.morePhotos": "+{{count}} de plus",
      "jobs.confirmDelete": "Êtes-vous sûr de vouloir supprimer cet emploi?",
      "jobs.confirmReject": "Êtes-vous sûr de vouloir rejeter cet emploi?",
      "jobs.rejectReason": "Raison du rejet (optionnel)",
      "jobs.completionNotes": "Notes de Fin",
      "jobs.enterNotes": "Entrez les notes de fin...",
      "jobs.loading": "Chargement des emplois...",
      
      // Reports - Additional keys
      "reports.payroll": "Rapport de Paie",
      "reports.userActivity": "Activité des Utilisateurs",
      "reports.jobStats": "Statistiques des Emplois",
      "reports.teamPerformance": "Performance de l'Équipe",
      "reports.timeEditor": "Éditeur de Temps",
      "reports.toolboxCompliance": "Conformité Boîte à Outils",
      "reports.deletedEntries": "Entrées Supprimées",
      "reports.totalHours": "Heures Totales",
      "reports.totalEntries": "Entrées Totales",
      "reports.activeUsers": "Utilisateurs Actifs",
      "reports.exportCSV": "Exporter en CSV",
      "reports.noData": "Aucune donnée disponible pour cette période",
      "reports.userName": "Nom d'Utilisateur",
      "reports.hours": "Heures",
      "reports.clockIns": "Pointages",
      "reports.avgHoursPerDay": "Heures Moy/Jour",
      "reports.jobsCompleted": "Emplois Terminés",
      "reports.teamName": "Nom de l'Équipe",
      "reports.memberCount": "Membres",
      "reports.avgHoursPerMember": "Heures Moy/Membre",
      "reports.completionRate": "Taux de Complétion",
      "reports.statusBreakdown": "Répartition par Statut",
      "reports.complianceRate": "Taux de Conformité",
      "reports.topicDate": "Date du Sujet",
      "reports.topicTitle": "Titre du Sujet",
      "reports.acknowledged": "Reconnu",
      "reports.notAcknowledged": "Non Reconnu",
      "reports.editEntry": "Modifier l'Entrée",
      "reports.deleteEntry": "Supprimer l'Entrée",
      "reports.date": "Date",
      "reports.clockIn": "Pointage",
      "reports.clockOut": "Dépointage",
      "reports.editedBy": "Modifié Par",
      "reports.editedAt": "Modifié Le",
      "reports.reason": "Raison",
      "reports.originalTime": "Temps Original",
      "reports.newTime": "Nouveau Temps",
      "reports.auditTrail": "Piste d'Audit",
      
      // Settings - Additional keys
      "settings.firstName": "Prénom",
      "settings.lastName": "Nom",
      "settings.email": "Email",
      "settings.phone": "Téléphone",
      "settings.address": "Adresse",
      "settings.role": "Rôle",
      "settings.updateProfile": "Mettre à Jour le Profil",
      "settings.uploadLogo": "Télécharger le Logo",
      "settings.companyName": "Nom de l'Entreprise",
      "settings.companyInfo": "Informations de l'Entreprise",
      "settings.notifications": "Notifications Email",
      "settings.notifyOnJobComplete": "Notifier les admins quand les emplois sont terminés",
      "settings.notifyCustomer": "Envoyer automatiquement un email aux clients à la fin de l'emploi",
      
      // Users Management - Additional keys
      "users.selectRole": "Sélectionner le rôle",
      "users.selectType": "Sélectionner le type",
      "users.status": "Statut",
      "users.active": "Actif",
      "users.inactive": "Inactif",
      "users.invitationSent": "Email d'invitation envoyé",
      "users.userCreated": "Utilisateur créé avec succès",
      "users.userUpdated": "Utilisateur mis à jour avec succès",
      "users.userDeleted": "Utilisateur supprimé avec succès",
      
      // Teams Management - Additional keys
      "teams.teamDescription": "Description de l'Équipe",
      "teams.selectSupervisor": "Sélectionner le superviseur",
      "teams.addMembers": "Ajouter des Membres",
      "teams.removeMembers": "Retirer des Membres",
      "teams.memberCount": "{{count}} membres",
      "teams.noMembers": "Aucun membre assigné",
      "teams.teamCreated": "Équipe créée avec succès",
      "teams.teamUpdated": "Équipe mise à jour avec succès",
      "teams.teamDeleted": "Équipe supprimée avec succès",
      
      // Billing & Payments
      "billing.title": "Facturation et Abonnement",
      "billing.subscription": "Détails de l'Abonnement",
      "billing.status": "Statut",
      "billing.plan": "Plan",
      "billing.activeUsers": "Utilisateurs Actifs",
      "billing.monthlyCost": "Coût Mensuel",
      "billing.nextBilling": "Prochaine Date de Facturation",
      "billing.setupFee": "Frais d'Installation",
      "billing.setupFeePaid": "Payé",
      "billing.setupFeePending": "Paiement Requis",
      "billing.paymentMethod": "Méthode de Paiement",
      "billing.updatePayment": "Mettre à Jour la Méthode de Paiement",
      "billing.viewInvoices": "Voir les Factures",
      "billing.cancelSubscription": "Annuler l'Abonnement",
      "billing.trialEnds": "L'essai se termine le {{date}}",
      "billing.perUserPerMonth": "par utilisateur/mois",
      "billing.currency": "Devise",
      "billing.setupPayment": "Configurer le Paiement",
      
      // Two-Factor Authentication
      "2fa.title": "Authentification à Deux Facteurs",
      "2fa.enable": "Activer 2FA",
      "2fa.disable": "Désactiver 2FA",
      "2fa.scanQR": "Scanner le Code QR",
      "2fa.enterCode": "Entrer le code de vérification",
      "2fa.backupCodes": "Codes de Secours",
      "2fa.saveBackupCodes": "Enregistrez ces codes de secours dans un endroit sûr",
      "2fa.verificationCode": "Code à 6 chiffres",
      "2fa.verify": "Vérifier et Activer",
      "2fa.enabled": "2FA est activé",
      "2fa.disabled": "2FA est désactivé",
      "2fa.required": "2FA est requis par votre entreprise",
      "2fa.optional": "2FA est optionnel",
      
      // Auth & Password - Additional keys
      "auth.enterEmail": "Entrez votre email",
      "auth.enterPassword": "Entrez votre mot de passe",
      "auth.resetPassword": "Réinitialiser le Mot de Passe",
      "auth.sendResetLink": "Envoyer le Lien de Réinitialisation",
      "auth.backToSignIn": "Retour à la Connexion",
      "auth.resetLinkSent": "Lien de réinitialisation envoyé à votre email",
      "auth.createNewPassword": "Créer un Nouveau Mot de Passe",
      "auth.passwordRequirements": "Le mot de passe doit contenir au moins 8 caractères",
      "auth.passwordMismatch": "Les mots de passe ne correspondent pas",
      "auth.invalidCredentials": "Email ou mot de passe invalide",
      "auth.accountLocked": "Le compte est verrouillé. Contactez l'administrateur.",
      "auth.sessionExpired": "Session expirée. Veuillez vous reconnecter.",
      "auth.firstLoginRequired": "Veuillez changer votre mot de passe à la première connexion",
      "auth.selectOrganization": "Sélectionner l'Organisation",
      "auth.multipleOrganizations": "Votre email est enregistré avec plusieurs organisations",
      
      // Map
      "map.title": "Carte de l'Équipe et des Emplois",
      "map.activeUsers": "Utilisateurs Actifs",
      "map.jobLocations": "Emplacements des Emplois",
      "map.noActiveUsers": "Aucun utilisateur actuellement pointé",
      "map.noJobs": "Aucun emploi avec emplacements",
      "map.clockedInAt": "Pointé à {{time}}",
      "map.lastUpdated": "Dernière mise à jour: {{time}}",
      "map.refreshMap": "Actualiser la Carte",
      "map.centerOnUser": "Centrer sur ma position",
      "map.showAllUsers": "Afficher tous les utilisateurs",
      "map.showAllJobs": "Afficher tous les emplois",
      "map.filterByTeam": "Filtrer par équipe",
      
      // Company Registration
      "register.title": "Enregistrez Votre Entreprise",
      "register.companyDetails": "Détails de l'Entreprise",
      "register.adminDetails": "Détails de l'Administrateur",
      "register.companyName": "Nom de l'Entreprise",
      "register.streetAddress": "Adresse",
      "register.city": "Ville",
      "register.state": "Province/État",
      "register.postalCode": "Code Postal",
      "register.country": "Pays",
      "register.adminFirstName": "Prénom de l'Admin",
      "register.adminLastName": "Nom de l'Admin",
      "register.adminEmail": "Email de l'Admin",
      "register.adminPhone": "Téléphone de l'Admin",
      "register.adminPassword": "Mot de Passe de l'Admin",
      "register.confirmPassword": "Confirmer le Mot de Passe",
      "register.agreeToTerms": "J'accepte les Conditions d'Utilisation et la Politique de Confidentialité",
      "register.createAccount": "Créer un Compte",
      "register.alreadyHaveAccount": "Vous avez déjà un compte?",
      "register.signInHere": "Connectez-vous ici",
      "register.companyRegistered": "Entreprise enregistrée avec succès!",
      
      // Time Editor
      "timeEditor.title": "Éditeur d'Entrée de Temps",
      "timeEditor.selectUser": "Sélectionner l'utilisateur à modifier",
      "timeEditor.selectDate": "Sélectionner la plage de dates",
      "timeEditor.fetchEntries": "Récupérer les Entrées",
      "timeEditor.noEntries": "Aucune entrée trouvée pour cette période",
      "timeEditor.editEntry": "Modifier l'Entrée",
      "timeEditor.bulkEdit": "Modification en Masse",
      "timeEditor.deleteEntry": "Supprimer l'Entrée",
      "timeEditor.editReason": "Raison de la Modification",
      "timeEditor.deleteReason": "Raison de la Suppression",
      "timeEditor.reasonRequired": "La raison est requise",
      "timeEditor.clockInTime": "Heure de Pointage",
      "timeEditor.clockOutTime": "Heure de Dépointage",
      "timeEditor.originalHours": "Original: {{hours}}",
      "timeEditor.newHours": "Nouveau: {{hours}}",
      "timeEditor.auditLog": "Journal d'Audit",
      "timeEditor.editedBy": "Modifié par {{name}} le {{date}}",
      "timeEditor.deletedBy": "Supprimé par {{name}} le {{date}}",
      "timeEditor.applyShift": "Appliquer la Durée du Quart",
      "timeEditor.shiftDuration": "Durée du Quart (heures)",
      "timeEditor.applyToAll": "Appliquer à toutes les entrées sélectionnées",
    }
  },
  es: {
    translation: {
      // Navigation
      "nav.home": "Inicio",
      "nav.jobs": "Trabajos",
      "nav.map": "Mapa",
      "nav.reports": "Informes",
      "nav.settings": "Configuración",
      "nav.signIn": "Iniciar Sesión",
      "nav.signOut": "Cerrar Sesión",
      "nav.getStarted": "Comenzar Gratis",
      "jobs.tab.paused": "Pausado",
      "jobs.tab.invoiced": "Facturado",
      "jobs.tab.archive": "Archivo",
      "jobs.searchPlaceholder": "Buscar por asignado, título, cliente…",
      "reports.subtitle2": "Información y datos completos",
      "nav.dashboard": "Panel",
      "nav.myDay": "Mi día",
      "nav.jobsBoard": "Tablero de trabajos",
      "nav.workplans": "Planes de trabajo",
      "nav.taskBoards": "Tableros de tareas",
      "nav.notes": "Notas",
      "nav.staff": "Personal",
      "nav.myTeam": "Mi equipo",
      "nav.teamManagement": "Gestión de equipos",
      "nav.timeOff": "Vacaciones",
      "nav.estimates": "Presupuestos",
      "nav.customers": "Clientes",
      "nav.projects": "Proyectos",
      "nav.billing": "Facturación",
      "nav.auditLog": "Registro de auditoría",
      "nav.archive": "Archivo",
      "nav.section.work": "Trabajo",
      "nav.section.people": "Personas",
      "nav.section.money": "Dinero",
      "nav.section.admin": "Admin",
      "nav.section.customers": "Clientes",
      "nav.section.pinned": "Fijados",
      "nav.approvals": "Aprobaciones",
      "nav.liveMap": "Mapa en vivo",
      "nav.payroll": "Nómina",
      "nav.payRuns": "Ciclos de nómina",
      "nav.reimbursements": "Reembolsos",
      "nav.invoices": "Facturas",
      "nav.onClock": "Trabajando",
      
      // Landing Page
      "landing.hero.title": "Seguimiento de Tiempo Laboral,",
      "landing.hero.subtitle": "Simplificado",
      "landing.hero.description": "Deje de perder tiempo en hojas de tiempo. Rastree horas, administre trabajos, verifique ubicaciones y procese nómina con una plataforma poderosa.",
      "landing.hero.startTrial": "Iniciar Prueba Gratuita de 30 Días",
      "landing.hero.watchDemo": "Ver Demo",
      "landing.hero.requestDemo": "Solicitar Demo",
      "landing.hero.noCard": "30 días gratis, luego facturación automática",
      "landing.hero.quickSetup": "Configuración en 5 minutos",
      
      // Features
      "features.timeTracking": "Seguimiento Inteligente del Tiempo",
      "features.timeTracking.desc": "Entrada/salida automatizada con verificaciones de bienestar y GPS",
      "features.location": "Verificación de Ubicación",
      "features.location.desc": "Asegure que los trabajadores estén en el sitio con rastreo GPS",
      "features.team": "Gestión de Equipos",
      "features.team.desc": "Organice equipos, asigne supervisores y administre toda su fuerza laboral",
      "features.reports": "Informes Avanzados",
      "features.reports.desc": "Informes de nómina, análisis de actividad y exportaciones CSV",
      "features.jobs": "Gestión de Trabajos",
      "features.jobs.desc": "Asigne trabajos, rastree progreso y requiera documentación fotográfica",
      "features.security": "Seguro y Conforme",
      "features.security.desc": "Seguridad de nivel empresarial con pistas de auditoría completas",
      
      // Auth
      "auth.welcomeBack": "Bienvenido de Nuevo",
      "auth.signInToTrack": "Inicie sesión para rastrear su tiempo",
      "auth.email": "Correo Electrónico",
      "auth.password": "Contraseña",
      "auth.signIn": "Iniciar Sesión",
      "auth.forgotPassword": "¿Olvidó su Contraseña?",
      "auth.newToHinesTime": "¿Nuevo en Hines Time?",
      "auth.registerCompany": "Registre Su Empresa",
      
      // Home/Dashboard
      "home.goodMorning": "Buenos Días",
      "home.goodAfternoon": "Buenas Tardes",
      "home.goodEvening": "Buenas Noches",
      "home.clockIn": "Fichar Entrada",
      "home.clockOut": "Fichar Salida",
      "home.clockedIn": "FICHADO",
      "home.readyToClock": "LISTO PARA FICHAR",
      "home.todayHours": "Horas de Hoy",
      "home.weekHours": "Horas de la Semana",
      "home.recentActivity": "Actividad Reciente",
      "home.yourJobs": "Sus Trabajos",
      
      // Supervisor Team Management
      "supervisor.teamManagement": "Gestión de Equipos",
      "supervisor.clockInTeam": "Fichar Equipo Entrada",
      "supervisor.clockOutTeam": "Fichar Equipo Salida",
      "supervisor.teamDescription": "Como supervisor, puede fichar entrada o salida de todo su equipo con una sola acción.",
      "supervisor.clockInResults": "Resultados de Fichaje de Equipo:",
      "supervisor.clockOutResults": "Resultados de Fichaje de Salida del Equipo:",
      "supervisor.clockedIn": "Fichados",
      "supervisor.clockedOut": "Fichados Salida",
      "supervisor.alreadyIn": "Ya Fichados",
      "supervisor.notClockedIn": "No Fichados",
      "supervisor.totalMembers": "Miembros Totales",
      
      // Wellness
      "wellness.title": "Verificación de Bienestar Requerida",
      "wellness.subtitle": "¡La Seguridad Primero!",
      "wellness.message": "Antes de fichar, confirme que está física y mentalmente listo para trabajar.",
      "wellness.confirm": "Confirmo que estoy apto para el trabajo y listo para trabajar de forma segura",
      "wellness.confirmButton": "✓ Confirmar y Fichar Entrada",
      "wellness.cancel": "Cancelar",
      
      // Toolbox
      "toolbox.title": "Reunión de Caja de Herramientas",
      "toolbox.readTopic": "Por favor lea el tema de seguridad de hoy antes de comenzar a trabajar",
      "toolbox.openPdf": "Abrir Documento PDF de Seguridad",
      "toolbox.iReadThis": "Leí Esto - Continuar Fichando",
      "toolbox.goBack": "Volver",
      "toolbox.question": "Verificación Rápida de Seguridad",
      "toolbox.didYouRead": "¿Leyó el tema de la caja de herramientas de hoy?",
      "toolbox.yesIRead": "Sí, Lo Leí",
      "toolbox.noIDidnt": "No, No Lo Hice",
      "toolbox.compliance": "Cumplimiento de Reuniones de Caja de Herramientas",
      
      // Jobs - Three-Tab System
      "jobs.title": "Trabajos",
      "jobs.addJob": "Agregar Trabajo",
      "jobs.startJob": "Iniciar Trabajo",
      "jobs.completeJob": "Completar Trabajo",
      "jobs.addPhotos": "Agregar Fotos",
      "jobs.viewPhotos": "Ver Fotos",
      "jobs.photosRequired": "Fotos Requeridas",
      "jobs.assignedTo": "Asignado a",
      "jobs.customer": "Cliente",
      "jobs.location": "Ubicación",
      "jobs.jobTitle": "Título del Trabajo",
      "jobs.jobDescription": "Descripción del Trabajo",
      "jobs.enterTitle": "Ingrese el título del trabajo",
      "jobs.enterDescription": "Ingrese la descripción del trabajo",
      "jobs.selectUser": "Seleccionar usuario",
      "jobs.customerName": "Nombre del cliente (opcional)",
      "jobs.customerEmail": "Email del cliente (opcional)",
      "jobs.streetAddress": "Dirección",
      "jobs.city": "Ciudad",
      "jobs.state": "Estado/Provincia",
      "jobs.postalCode": "Código Postal",
      "jobs.country": "País",
      "jobs.selectCountry": "Seleccionar país",
      "jobs.selectState": "Seleccionar estado",
      "jobs.createJob": "Crear Trabajo",
      "jobs.cancel": "Cancelar",
      "jobs.editJob": "Editar Trabajo",
      "jobs.deleteJob": "Eliminar Trabajo",
      "jobs.reassignJob": "Reasignar Trabajo",
      "jobs.sendReminder": "Enviar Recordatorio",
      "jobs.acceptJob": "Aceptar Trabajo",
      "jobs.rejectJob": "Rechazar Trabajo",
      "jobs.viewOnMap": "Ver en el Mapa",
      "jobs.uploadPhotos": "Subir Fotos",
      "jobs.emailPhotos": "Enviar Fotos por Email",
      "jobs.emailReport": "Enviar Informe por Email",
      "jobs.viewStats": "Ver Estadísticas",
      "jobs.tab.todo": "Por Hacer",
      "jobs.tab.inProgress": "En Progreso",
      "jobs.tab.completed": "Completados",
      "jobs.status.pending": "Pendiente",
      "jobs.status.accepted": "Aceptado",
      "jobs.status.rejected": "Rechazado",
      "jobs.status.inProgress": "En Progreso",
      "jobs.status.completed": "Completado",
      "jobs.overdue": "VENCIDO",
      "jobs.duration": "Duración: {{duration}}",
      "jobs.completedBy": "Completado por {{name}}",
      "jobs.noJobs": "No se encontraron trabajos",
      "jobs.noTodoJobs": "No hay trabajos pendientes",
      "jobs.noInProgressJobs": "No hay trabajos en progreso",
      "jobs.noCompletedJobs": "No hay trabajos completados",
      
      // Reports
      "reports.title": "Informes y Análisis",
      "reports.subtitle": "Información y datos completos",
      "reports.supervisorView": "Vista de Supervisor - Informes Específicos del Equipo",
      "reports.supervisorDescription": "Está viendo informes filtrados solo para los miembros de su equipo. Los datos que se muestran a continuación incluyen solo actividades, horas y trabajos relacionados con su equipo asignado.",
      "reports.yourTeam": "Su Equipo:",
      "reports.noTeamAssigned": "Actualmente no está asignado a ningún equipo. Por favor contacte a su administrador.",
      "reports.refresh": "Actualizar",
      "reports.generateReports": "Generar Informes",
      "reports.startDate": "Fecha de Inicio",
      "reports.endDate": "Fecha de Fin",
      
      // Settings
      "settings.title": "Configuración",
      "settings.profile": "Información de Perfil",
      "settings.editProfile": "Editar Perfil",
      "settings.changePassword": "Cambiar Contraseña",
      "settings.currentPassword": "Contraseña Actual",
      "settings.newPassword": "Nueva Contraseña",
      "settings.confirmPassword": "Confirmar Contraseña",
      "settings.management": "Gestión",
      "settings.manageUsers": "Gestionar Usuarios",
      "settings.manageTeams": "Gestionar Equipos",
      "settings.toolboxTopics": "Temas de Reunión de Caja de Herramientas",
      "settings.billing": "Facturación y Suscripción",
      "settings.companyBranding": "Marca de la Empresa",
      "settings.twoFactor": "Autenticación de Dos Factores",
      "settings.userManual": "Manual del Usuario",
      "settings.termsOfService": "Términos de Servicio",
      "settings.privacyPolicy": "Política de Privacidad",
      
      // Users Management
      "users.title": "Gestionar Usuarios",
      "users.addUser": "Agregar Usuario",
      "users.editUser": "Editar Usuario",
      "users.deleteUser": "Eliminar Usuario",
      "users.resendInvitation": "Reenviar Invitación",
      "users.firstName": "Nombre",
      "users.lastName": "Apellido",
      "users.email": "Dirección de Email",
      "users.phone": "Número de Teléfono",
      "users.role": "Rol",
      "users.userType": "Tipo de Usuario",
      "users.role.admin": "Administrador",
      "users.role.supervisor": "Supervisor",
      "users.role.user": "Usuario",
      "users.type.employee": "Empleado",
      "users.type.contractor": "Contratista",
      "users.confirmDelete": "¿Está seguro de que desea eliminar este usuario?",
      
      // Teams Management
      "teams.title": "Gestionar Equipos",
      "teams.addTeam": "Agregar Equipo",
      "teams.editTeam": "Editar Equipo",
      "teams.deleteTeam": "Eliminar Equipo",
      "teams.teamName": "Nombre del Equipo",
      "teams.supervisor": "Supervisor",
      "teams.members": "Miembros del Equipo",
      "teams.confirmDelete": "¿Está seguro de que desea eliminar este equipo?",
      
      // Common
      "common.save": "Guardar",
      "common.cancel": "Cancelar",
      "common.delete": "Eliminar",
      "common.edit": "Editar",
      "common.close": "Cerrar",
      "common.submit": "Enviar",
      "common.loading": "Cargando...",
      "common.success": "Éxito",
      "common.error": "Error",
      "common.confirm": "Confirmar",
      "common.yes": "Sí",
      "common.no": "No",
      
      // Messages
      "message.success.clockIn": "¡Fichado con éxito!",
      "message.success.clockOut": "¡Salida fichada con éxito!",
      "message.error.clockIn": "No se puede fichar. Por favor, inténtelo de nuevo.",
      "message.error.clockOut": "No se puede fichar salida. Por favor, inténtelo de nuevo.",
      "message.success.jobCreated": "¡Trabajo creado con éxito!",
      "message.success.jobUpdated": "¡Trabajo actualizado con éxito!",
      "message.success.jobDeleted": "¡Trabajo eliminado con éxito!",
      "message.success.jobStarted": "¡Trabajo iniciado con éxito!",
      "message.success.jobCompleted": "¡Trabajo completado con éxito!",
      "message.success.photoUploaded": "¡Foto subida con éxito!",
      "message.success.photoDeleted": "¡Foto eliminada con éxito!",
      "message.success.profileUpdated": "¡Perfil actualizado con éxito!",
      "message.success.passwordChanged": "¡Contraseña cambiada con éxito!",
      "message.success.settingsSaved": "¡Configuración guardada con éxito!",
      "message.error.uploadFailed": "Error al subir. Por favor, inténtelo de nuevo.",
      "message.error.locationRequired": "Se requiere acceso a la ubicación para esta función.",
      "message.error.tooFarFromJob": "Está demasiado lejos de la ubicación del trabajo.",
      "message.error.photosRequired": "Por favor suba las fotos requeridas antes de completar.",
      "message.error.invalidInput": "Por favor verifique su entrada e inténtelo de nuevo.",
      "message.error.serverError": "Error del servidor. Por favor, inténtelo más tarde.",
      "message.error.unauthorized": "No tiene permiso para realizar esta acción.",
      "message.warning.unsavedChanges": "Tiene cambios sin guardar.",
      "message.confirm.delete": "Esta acción no se puede deshacer.",
      
      // Jobs - Additional keys
      "jobs.gpsCoordinates": "Coordenadas GPS (Opcional)",
      "jobs.latitude": "Latitud",
      "jobs.longitude": "Longitud",
      "jobs.requirePhotos": "Requerir fotos de finalización del trabajo",
      "jobs.requireLocation": "Requerir verificación de ubicación",
      "jobs.locationHelp": "El trabajador debe estar a menos de 500m del sitio para comenzar",
      "jobs.noLocationHelp": "El trabajador puede comenzar el trabajo desde cualquier lugar",
      "jobs.assignedAgo": "Asignado hace {{time}}",
      "jobs.startedAt": "Iniciado a las {{time}}",
      "jobs.completedAt": "Completado a las {{time}}",
      "jobs.photos": "{{count}} fotos",
      "jobs.noPhotos": "Sin fotos",
      "jobs.photosCount": "{{count}}/{{total}} fotos",
      "jobs.morePhotos": "+{{count}} más",
      "jobs.confirmDelete": "¿Está seguro de que desea eliminar este trabajo?",
      "jobs.confirmReject": "¿Está seguro de que desea rechazar este trabajo?",
      "jobs.rejectReason": "Razón del rechazo (opcional)",
      "jobs.completionNotes": "Notas de Finalización",
      "jobs.enterNotes": "Ingrese notas de finalización...",
      "jobs.loading": "Cargando trabajos...",
      
      // Reports - Additional keys
      "reports.payroll": "Informe de Nómina",
      "reports.userActivity": "Actividad de Usuarios",
      "reports.jobStats": "Estadísticas de Trabajos",
      "reports.teamPerformance": "Rendimiento del Equipo",
      "reports.timeEditor": "Editor de Tiempo",
      "reports.toolboxCompliance": "Cumplimiento de Caja de Herramientas",
      "reports.deletedEntries": "Entradas Eliminadas",
      "reports.totalHours": "Horas Totales",
      "reports.totalEntries": "Entradas Totales",
      "reports.activeUsers": "Usuarios Activos",
      "reports.exportCSV": "Exportar a CSV",
      "reports.noData": "No hay datos disponibles para este período",
      "reports.userName": "Nombre de Usuario",
      "reports.hours": "Horas",
      "reports.clockIns": "Fichajes",
      "reports.avgHoursPerDay": "Horas Prom/Día",
      "reports.jobsCompleted": "Trabajos Completados",
      "reports.teamName": "Nombre del Equipo",
      "reports.memberCount": "Miembros",
      "reports.avgHoursPerMember": "Horas Prom/Miembro",
      "reports.completionRate": "Tasa de Finalización",
      "reports.statusBreakdown": "Desglose por Estado",
      "reports.complianceRate": "Tasa de Cumplimiento",
      "reports.topicDate": "Fecha del Tema",
      "reports.topicTitle": "Título del Tema",
      "reports.acknowledged": "Reconocido",
      "reports.notAcknowledged": "No Reconocido",
      "reports.editEntry": "Editar Entrada",
      "reports.deleteEntry": "Eliminar Entrada",
      "reports.date": "Fecha",
      "reports.clockIn": "Fichaje",
      "reports.clockOut": "Fichaje Salida",
      "reports.editedBy": "Editado Por",
      "reports.editedAt": "Editado El",
      "reports.reason": "Razón",
      "reports.originalTime": "Tiempo Original",
      "reports.newTime": "Nuevo Tiempo",
      "reports.auditTrail": "Rastro de Auditoría",
      
      // Settings - Additional keys
      "settings.firstName": "Nombre",
      "settings.lastName": "Apellido",
      "settings.email": "Email",
      "settings.phone": "Teléfono",
      "settings.address": "Dirección",
      "settings.role": "Rol",
      "settings.updateProfile": "Actualizar Perfil",
      "settings.uploadLogo": "Subir Logo",
      "settings.companyName": "Nombre de la Empresa",
      "settings.companyInfo": "Información de la Empresa",
      "settings.notifications": "Notificaciones por Email",
      "settings.notifyOnJobComplete": "Notificar a admins cuando los trabajos se completan",
      "settings.notifyCustomer": "Enviar automáticamente email a clientes al completar trabajo",
      
      // Users Management - Additional keys
      "users.selectRole": "Seleccionar rol",
      "users.selectType": "Seleccionar tipo",
      "users.status": "Estado",
      "users.active": "Activo",
      "users.inactive": "Inactivo",
      "users.invitationSent": "Email de invitación enviado",
      "users.userCreated": "Usuario creado con éxito",
      "users.userUpdated": "Usuario actualizado con éxito",
      "users.userDeleted": "Usuario eliminado con éxito",
      
      // Teams Management - Additional keys
      "teams.teamDescription": "Descripción del Equipo",
      "teams.selectSupervisor": "Seleccionar supervisor",
      "teams.addMembers": "Agregar Miembros",
      "teams.removeMembers": "Quitar Miembros",
      "teams.memberCount": "{{count}} miembros",
      "teams.noMembers": "Sin miembros asignados",
      "teams.teamCreated": "Equipo creado con éxito",
      "teams.teamUpdated": "Equipo actualizado con éxito",
      "teams.teamDeleted": "Equipo eliminado con éxito",
      
      // Billing & Payments
      "billing.title": "Facturación y Suscripción",
      "billing.subscription": "Detalles de Suscripción",
      "billing.status": "Estado",
      "billing.plan": "Plan",
      "billing.activeUsers": "Usuarios Activos",
      "billing.monthlyCost": "Costo Mensual",
      "billing.nextBilling": "Próxima Fecha de Facturación",
      "billing.setupFee": "Tarifa de Instalación",
      "billing.setupFeePaid": "Pagado",
      "billing.setupFeePending": "Pago Requerido",
      "billing.paymentMethod": "Método de Pago",
      "billing.updatePayment": "Actualizar Método de Pago",
      "billing.viewInvoices": "Ver Facturas",
      "billing.cancelSubscription": "Cancelar Suscripción",
      "billing.trialEnds": "La prueba termina el {{date}}",
      "billing.perUserPerMonth": "por usuario/mes",
      "billing.currency": "Moneda",
      "billing.setupPayment": "Configurar Pago",
      
      // Two-Factor Authentication
      "2fa.title": "Autenticación de Dos Factores",
      "2fa.enable": "Activar 2FA",
      "2fa.disable": "Desactivar 2FA",
      "2fa.scanQR": "Escanear Código QR",
      "2fa.enterCode": "Ingresar código de verificación",
      "2fa.backupCodes": "Códigos de Respaldo",
      "2fa.saveBackupCodes": "Guarde estos códigos de respaldo en un lugar seguro",
      "2fa.verificationCode": "Código de 6 dígitos",
      "2fa.verify": "Verificar y Activar",
      "2fa.enabled": "2FA está activado",
      "2fa.disabled": "2FA está desactivado",
      "2fa.required": "2FA es requerido por su empresa",
      "2fa.optional": "2FA es opcional",
      
      // Auth & Password - Additional keys
      "auth.enterEmail": "Ingrese su email",
      "auth.enterPassword": "Ingrese su contraseña",
      "auth.resetPassword": "Restablecer Contraseña",
      "auth.sendResetLink": "Enviar Enlace de Restablecimiento",
      "auth.backToSignIn": "Volver a Iniciar Sesión",
      "auth.resetLinkSent": "Enlace de restablecimiento enviado a su email",
      "auth.createNewPassword": "Crear Nueva Contraseña",
      "auth.passwordRequirements": "La contraseña debe tener al menos 8 caracteres",
      "auth.passwordMismatch": "Las contraseñas no coinciden",
      "auth.invalidCredentials": "Email o contraseña inválidos",
      "auth.accountLocked": "La cuenta está bloqueada. Contacte al administrador.",
      "auth.sessionExpired": "Sesión expirada. Por favor inicie sesión de nuevo.",
      "auth.firstLoginRequired": "Por favor cambie su contraseña en el primer inicio de sesión",
      "auth.selectOrganization": "Seleccionar Organización",
      "auth.multipleOrganizations": "Su email está registrado con múltiples organizaciones",
      
      // Map
      "map.title": "Mapa de Equipo y Trabajos",
      "map.activeUsers": "Usuarios Activos",
      "map.jobLocations": "Ubicaciones de Trabajos",
      "map.noActiveUsers": "No hay usuarios fichados actualmente",
      "map.noJobs": "No hay trabajos con ubicaciones",
      "map.clockedInAt": "Fichado a las {{time}}",
      "map.lastUpdated": "Última actualización: {{time}}",
      "map.refreshMap": "Actualizar Mapa",
      "map.centerOnUser": "Centrar en mi ubicación",
      "map.showAllUsers": "Mostrar todos los usuarios",
      "map.showAllJobs": "Mostrar todos los trabajos",
      "map.filterByTeam": "Filtrar por equipo",
      
      // Company Registration
      "register.title": "Registre Su Empresa",
      "register.companyDetails": "Detalles de la Empresa",
      "register.adminDetails": "Detalles del Administrador",
      "register.companyName": "Nombre de la Empresa",
      "register.streetAddress": "Dirección",
      "register.city": "Ciudad",
      "register.state": "Estado/Provincia",
      "register.postalCode": "Código Postal",
      "register.country": "País",
      "register.adminFirstName": "Nombre del Admin",
      "register.adminLastName": "Apellido del Admin",
      "register.adminEmail": "Email del Admin",
      "register.adminPhone": "Teléfono del Admin",
      "register.adminPassword": "Contraseña del Admin",
      "register.confirmPassword": "Confirmar Contraseña",
      "register.agreeToTerms": "Acepto los Términos de Servicio y la Política de Privacidad",
      "register.createAccount": "Crear Cuenta",
      "register.alreadyHaveAccount": "¿Ya tiene una cuenta?",
      "register.signInHere": "Inicie sesión aquí",
      "register.companyRegistered": "¡Empresa registrada con éxito!",
      
      // Time Editor
      "timeEditor.title": "Editor de Entrada de Tiempo",
      "timeEditor.selectUser": "Seleccionar usuario para editar",
      "timeEditor.selectDate": "Seleccionar rango de fechas",
      "timeEditor.fetchEntries": "Obtener Entradas",
      "timeEditor.noEntries": "No se encontraron entradas para este período",
      "timeEditor.editEntry": "Editar Entrada",
      "timeEditor.bulkEdit": "Edición Masiva",
      "timeEditor.deleteEntry": "Eliminar Entrada",
      "timeEditor.editReason": "Razón de la Edición",
      "timeEditor.deleteReason": "Razón de la Eliminación",
      "timeEditor.reasonRequired": "Se requiere una razón",
      "timeEditor.clockInTime": "Hora de Fichaje",
      "timeEditor.clockOutTime": "Hora de Fichaje Salida",
      "timeEditor.originalHours": "Original: {{hours}}",
      "timeEditor.newHours": "Nuevo: {{hours}}",
      "timeEditor.auditLog": "Registro de Auditoría",
      "timeEditor.editedBy": "Editado por {{name}} el {{date}}",
      "timeEditor.deletedBy": "Eliminado por {{name}} el {{date}}",
      "timeEditor.applyShift": "Aplicar Duración del Turno",
      "timeEditor.shiftDuration": "Duración del Turno (horas)",
      "timeEditor.applyToAll": "Aplicar a todas las entradas seleccionadas",
    }
  },
  de: {
    translation: {
      // Navigation
      "nav.home": "Startseite",
      "nav.jobs": "Aufträge",
      "nav.map": "Karte",
      "nav.reports": "Berichte",
      "nav.settings": "Einstellungen",
      "nav.signIn": "Anmelden",
      "nav.signOut": "Abmelden",
      "nav.getStarted": "Kostenlos Starten",
      "jobs.tab.paused": "Pausiert",
      "jobs.tab.invoiced": "Abgerechnet",
      "jobs.tab.archive": "Archiv",
      "jobs.searchPlaceholder": "Suche nach Mitarbeiter, Titel, Kunde…",
      "reports.subtitle2": "Umfassende Einblicke und Daten",
      "nav.dashboard": "Übersicht",
      "nav.myDay": "Mein Tag",
      "nav.jobsBoard": "Auftragstafel",
      "nav.workplans": "Arbeitspläne",
      "nav.taskBoards": "Aufgabentafeln",
      "nav.notes": "Notizen",
      "nav.staff": "Personal",
      "nav.myTeam": "Mein Team",
      "nav.teamManagement": "Teamverwaltung",
      "nav.timeOff": "Abwesenheit",
      "nav.estimates": "Angebote",
      "nav.customers": "Kunden",
      "nav.projects": "Projekte",
      "nav.billing": "Abrechnung",
      "nav.auditLog": "Prüfprotokoll",
      "nav.archive": "Archiv",
      "nav.section.work": "Arbeit",
      "nav.section.people": "Personen",
      "nav.section.money": "Finanzen",
      "nav.section.admin": "Verwaltung",
      "nav.section.customers": "Kunden",
      "nav.section.pinned": "Angeheftet",
      "nav.approvals": "Freigaben",
      "nav.liveMap": "Live-Karte",
      "nav.payroll": "Lohnabrechnung",
      "nav.invoices": "Rechnungen",
      "nav.onClock": "Eingestempelt",

      // Landing Page
      "landing.hero.title": "Arbeitszeiterfassung,",
      "landing.hero.subtitle": "Einfach Gemacht",
      "landing.hero.description": "Verschwenden Sie keine Zeit mehr mit Stundenzetteln. Erfassen Sie Stunden, verwalten Sie Aufträge, prüfen Sie Standorte und erledigen Sie die Lohnabrechnung mit einer Plattform.",
      "landing.hero.startTrial": "30 Tage Kostenlos Testen",
      "landing.hero.watchDemo": "Demo Ansehen",
      "landing.hero.requestDemo": "Demo Anfragen",
      "landing.hero.noCard": "30 Tage kostenlos, danach automatische Abrechnung",
      "landing.hero.quickSetup": "Einrichtung in 5 Minuten",

      // Features
      "features.timeTracking": "Intelligente Zeiterfassung",
      "features.timeTracking.desc": "Automatisches Ein-/Ausstempeln mit Sicherheitschecks und GPS-Verifizierung",
      "features.location": "Standortprüfung",
      "features.location.desc": "Stellen Sie mit GPS sicher, dass Mitarbeiter vor Ort sind",
      "features.team": "Teamverwaltung",
      "features.team.desc": "Organisieren Sie Teams, weisen Sie Vorgesetzte zu und verwalten Sie Ihre gesamte Belegschaft",
      "features.reports": "Erweiterte Berichte",
      "features.reports.desc": "Lohnberichte, Aktivitätsanalysen und CSV-Exporte für die Buchhaltung",
      "features.jobs": "Auftragsverwaltung",
      "features.jobs.desc": "Aufträge zuweisen, Fortschritt verfolgen und Fotodokumentation verlangen",
      "features.security": "Sicher & Konform",
      "features.security.desc": "Sicherheit auf Unternehmensniveau mit vollständigen Prüfpfaden",

      // Auth
      "auth.welcomeBack": "Willkommen Zurück",
      "auth.signInToTrack": "Melden Sie sich an, um Ihre Zeit zu erfassen",
      "auth.email": "E-Mail",
      "auth.password": "Passwort",
      "auth.signIn": "Anmelden",
      "auth.forgotPassword": "Passwort vergessen?",
      "auth.newToHinesTime": "Neu bei Hines Time?",
      "auth.registerCompany": "Firma Registrieren",
      "auth.enterEmail": "E-Mail eingeben",
      "auth.enterPassword": "Passwort eingeben",
      "auth.resetPassword": "Passwort Zurücksetzen",
      "auth.sendResetLink": "Link Senden",
      "auth.backToSignIn": "Zurück zur Anmeldung",
      "auth.resetLinkSent": "Link zum Zurücksetzen wurde an Ihre E-Mail gesendet",
      "auth.createNewPassword": "Neues Passwort Erstellen",
      "auth.passwordRequirements": "Das Passwort muss mindestens 8 Zeichen lang sein",
      "auth.passwordMismatch": "Passwörter stimmen nicht überein",
      "auth.invalidCredentials": "Ungültige E-Mail oder Passwort",
      "auth.accountLocked": "Konto ist gesperrt. Kontaktieren Sie den Administrator.",
      "auth.sessionExpired": "Sitzung abgelaufen. Bitte erneut anmelden.",
      "auth.firstLoginRequired": "Bitte ändern Sie Ihr Passwort bei der ersten Anmeldung",
      "auth.selectOrganization": "Organisation Auswählen",
      "auth.multipleOrganizations": "Ihre E-Mail ist bei mehreren Organisationen registriert",

      // Home/Dashboard
      "home.goodMorning": "Guten Morgen",
      "home.goodAfternoon": "Guten Tag",
      "home.goodEvening": "Guten Abend",
      "home.clockIn": "Einstempeln",
      "home.clockOut": "Ausstempeln",
      "home.clockedIn": "EINGESTEMPELT",
      "home.readyToClock": "BEREIT ZUM EINSTEMPELN",
      "home.todayHours": "Heutige Stunden",
      "home.weekHours": "Wochenstunden",
      "home.recentActivity": "Letzte Aktivität",
      "home.yourJobs": "Ihre Aufträge",

      // Supervisor Team Management
      "supervisor.teamManagement": "Teamverwaltung",
      "supervisor.clockInTeam": "Team Einstempeln",
      "supervisor.clockOutTeam": "Team Ausstempeln",
      "supervisor.teamDescription": "Als Vorgesetzter können Sie Ihr gesamtes Team mit einer Aktion ein- oder ausstempeln.",
      "supervisor.clockInResults": "Team-Einstempel-Ergebnisse:",
      "supervisor.clockOutResults": "Team-Ausstempel-Ergebnisse:",
      "supervisor.clockedIn": "Eingestempelt",
      "supervisor.clockedOut": "Ausgestempelt",
      "supervisor.alreadyIn": "Bereits Eingestempelt",
      "supervisor.notClockedIn": "Nicht Eingestempelt",
      "supervisor.totalMembers": "Mitglieder Gesamt",

      // Wellness
      "wellness.title": "Gesundheitscheck Erforderlich",
      "wellness.subtitle": "Sicherheit Zuerst!",
      "wellness.message": "Bitte bestätigen Sie vor dem Einstempeln, dass Sie körperlich und geistig arbeitsbereit sind.",
      "wellness.confirm": "Ich bestätige, dass ich arbeitsfähig und bereit bin, sicher zu arbeiten",
      "wellness.confirmButton": "✓ Bestätigen & Einstempeln",
      "wellness.cancel": "Abbrechen",

      // Toolbox
      "toolbox.title": "Sicherheitsbesprechung",
      "toolbox.readTopic": "Bitte lesen Sie das heutige Sicherheitsthema vor Arbeitsbeginn",
      "toolbox.openPdf": "Sicherheits-PDF Öffnen",
      "toolbox.iReadThis": "Gelesen - Weiter Einstempeln",
      "toolbox.goBack": "Zurück",
      "toolbox.question": "Kurzer Sicherheitscheck",
      "toolbox.didYouRead": "Haben Sie das heutige Sicherheitsthema gelesen?",
      "toolbox.yesIRead": "Ja, Gelesen",
      "toolbox.noIDidnt": "Nein, Nicht Gelesen",
      "toolbox.compliance": "Einhaltung der Sicherheitsbesprechungen",

      // Jobs
      "jobs.title": "Aufträge",
      "jobs.addJob": "Auftrag Hinzufügen",
      "jobs.startJob": "Auftrag Starten",
      "jobs.completeJob": "Auftrag Abschließen",
      "jobs.addPhotos": "Fotos Hinzufügen",
      "jobs.viewPhotos": "Fotos Ansehen",
      "jobs.photosRequired": "Fotos Erforderlich",
      "jobs.assignedTo": "Zugewiesen an",
      "jobs.customer": "Kunde",
      "jobs.location": "Standort",
      "jobs.jobTitle": "Auftragstitel",
      "jobs.jobDescription": "Auftragsbeschreibung",
      "jobs.enterTitle": "Auftragstitel eingeben",
      "jobs.enterDescription": "Auftragsbeschreibung eingeben",
      "jobs.selectUser": "Benutzer auswählen",
      "jobs.customerName": "Kundenname (optional)",
      "jobs.customerEmail": "Kunden-E-Mail (optional)",
      "jobs.streetAddress": "Straße",
      "jobs.city": "Stadt",
      "jobs.state": "Bundesland/Region",
      "jobs.postalCode": "Postleitzahl",
      "jobs.country": "Land",
      "jobs.selectCountry": "Land auswählen",
      "jobs.selectState": "Region auswählen",
      "jobs.gpsCoordinates": "GPS-Koordinaten (Optional)",
      "jobs.latitude": "Breitengrad",
      "jobs.longitude": "Längengrad",
      "jobs.requirePhotos": "Abschlussfotos verlangen",
      "jobs.requireLocation": "Standortprüfung verlangen",
      "jobs.locationHelp": "Mitarbeiter muss sich innerhalb von 500m der Baustelle befinden",
      "jobs.noLocationHelp": "Mitarbeiter kann von überall aus beginnen",
      "jobs.createJob": "Auftrag Erstellen",
      "jobs.cancel": "Abbrechen",
      "jobs.editJob": "Auftrag Bearbeiten",
      "jobs.deleteJob": "Auftrag Löschen",
      "jobs.reassignJob": "Auftrag Neu Zuweisen",
      "jobs.sendReminder": "Erinnerung Senden",
      "jobs.acceptJob": "Auftrag Annehmen",
      "jobs.rejectJob": "Auftrag Ablehnen",
      "jobs.viewOnMap": "Auf Karte Ansehen",
      "jobs.uploadPhotos": "Fotos Hochladen",
      "jobs.emailPhotos": "Fotos per E-Mail Senden",
      "jobs.emailReport": "Bericht per E-Mail Senden",
      "jobs.viewStats": "Statistiken Ansehen",
      "jobs.tab.todo": "Zu Erledigen",
      "jobs.tab.inProgress": "In Bearbeitung",
      "jobs.tab.completed": "Abgeschlossen",
      "jobs.status.pending": "Ausstehend",
      "jobs.status.accepted": "Angenommen",
      "jobs.status.rejected": "Abgelehnt",
      "jobs.status.inProgress": "In Bearbeitung",
      "jobs.status.completed": "Abgeschlossen",
      "jobs.overdue": "ÜBERFÄLLIG",
      "jobs.assignedAgo": "Zugewiesen vor {{time}}",
      "jobs.duration": "Dauer: {{duration}}",
      "jobs.completedBy": "Abgeschlossen von {{name}}",
      "jobs.startedAt": "Begonnen um {{time}}",
      "jobs.completedAt": "Abgeschlossen um {{time}}",
      "jobs.photos": "{{count}} Fotos",
      "jobs.noPhotos": "Keine Fotos",
      "jobs.photosCount": "{{count}}/{{total}} Fotos",
      "jobs.morePhotos": "+{{count}} weitere",
      "jobs.confirmDelete": "Möchten Sie diesen Auftrag wirklich löschen?",
      "jobs.confirmReject": "Möchten Sie diesen Auftrag wirklich ablehnen?",
      "jobs.rejectReason": "Ablehnungsgrund (optional)",
      "jobs.completionNotes": "Abschlussnotizen",
      "jobs.enterNotes": "Abschlussnotizen eingeben...",
      "jobs.loading": "Aufträge werden geladen...",
      "jobs.noJobs": "Keine Aufträge gefunden",
      "jobs.noTodoJobs": "Keine ausstehenden Aufträge",
      "jobs.noInProgressJobs": "Keine Aufträge in Bearbeitung",
      "jobs.noCompletedJobs": "Keine abgeschlossenen Aufträge",

      // Reports
      "reports.title": "Berichte & Analysen",
      "reports.subtitle": "Umfassende Einblicke und Daten",
      "reports.supervisorView": "Vorgesetzten-Ansicht - Teamspezifische Berichte",
      "reports.supervisorDescription": "Sie sehen Berichte, die nur für Ihre Teammitglieder gefiltert sind. Die angezeigten Daten umfassen nur Aktivitäten, Stunden und Aufträge Ihres Teams.",
      "reports.yourTeam": "Ihr Team:",
      "reports.noTeamAssigned": "Sie sind derzeit keinem Team zugeordnet. Bitte kontaktieren Sie Ihren Administrator.",
      "reports.refresh": "Aktualisieren",
      "reports.generateReports": "Berichte Erstellen",
      "reports.startDate": "Startdatum",
      "reports.endDate": "Enddatum",
      "reports.payroll": "Lohnbericht",
      "reports.userActivity": "Benutzeraktivität",
      "reports.jobStats": "Auftragsstatistiken",
      "reports.teamPerformance": "Teamleistung",
      "reports.timeEditor": "Zeiteditor",
      "reports.toolboxCompliance": "Sicherheitsbesprechungs-Einhaltung",
      "reports.deletedEntries": "Gelöschte Einträge",
      "reports.totalHours": "Gesamtstunden",
      "reports.totalEntries": "Gesamteinträge",
      "reports.activeUsers": "Aktive Benutzer",
      "reports.exportCSV": "Als CSV Exportieren",
      "reports.noData": "Keine Daten für diesen Zeitraum verfügbar",
      "reports.userName": "Benutzername",
      "reports.hours": "Stunden",
      "reports.clockIns": "Einstempelungen",
      "reports.avgHoursPerDay": "Ø Stunden/Tag",
      "reports.jobsCompleted": "Abgeschlossene Aufträge",
      "reports.teamName": "Teamname",
      "reports.memberCount": "Mitglieder",
      "reports.avgHoursPerMember": "Ø Stunden/Mitglied",
      "reports.completionRate": "Abschlussquote",
      "reports.statusBreakdown": "Statusaufschlüsselung",
      "reports.complianceRate": "Einhaltungsquote",
      "reports.date": "Datum",
      "reports.clockIn": "Einstempeln",
      "reports.clockOut": "Ausstempeln",
      "reports.editedBy": "Bearbeitet Von",
      "reports.editedAt": "Bearbeitet Am",
      "reports.reason": "Grund",
      "reports.auditTrail": "Prüfpfad",

      // Settings
      "settings.title": "Einstellungen",
      "settings.profile": "Profilinformationen",
      "settings.editProfile": "Profil Bearbeiten",
      "settings.changePassword": "Passwort Ändern",
      "settings.currentPassword": "Aktuelles Passwort",
      "settings.newPassword": "Neues Passwort",
      "settings.confirmPassword": "Passwort Bestätigen",
      "settings.management": "Verwaltung",
      "settings.manageUsers": "Benutzer Verwalten",
      "settings.manageTeams": "Teams Verwalten",
      "settings.toolboxTopics": "Sicherheitsbesprechungs-Themen",
      "settings.billing": "Abrechnung & Abonnement",
      "settings.companyBranding": "Firmen-Branding",
      "settings.twoFactor": "Zwei-Faktor-Authentifizierung",
      "settings.userManual": "Benutzerhandbuch",
      "settings.termsOfService": "Nutzungsbedingungen",
      "settings.privacyPolicy": "Datenschutzerklärung",
      "settings.firstName": "Vorname",
      "settings.lastName": "Nachname",
      "settings.email": "E-Mail",
      "settings.phone": "Telefon",
      "settings.address": "Adresse",
      "settings.role": "Rolle",
      "settings.updateProfile": "Profil Aktualisieren",
      "settings.uploadLogo": "Logo Hochladen",
      "settings.companyName": "Firmenname",
      "settings.companyInfo": "Firmeninformationen",
      "settings.notifications": "E-Mail-Benachrichtigungen",

      // Users Management
      "users.title": "Benutzer Verwalten",
      "users.addUser": "Benutzer Hinzufügen",
      "users.editUser": "Benutzer Bearbeiten",
      "users.deleteUser": "Benutzer Löschen",
      "users.resendInvitation": "Einladung Erneut Senden",
      "users.firstName": "Vorname",
      "users.lastName": "Nachname",
      "users.email": "E-Mail-Adresse",
      "users.phone": "Telefonnummer",
      "users.role": "Rolle",
      "users.userType": "Benutzertyp",
      "users.selectRole": "Rolle auswählen",
      "users.selectType": "Typ auswählen",
      "users.role.admin": "Administrator",
      "users.role.supervisor": "Vorgesetzter",
      "users.role.user": "Benutzer",
      "users.type.employee": "Angestellter",
      "users.type.contractor": "Auftragnehmer",
      "users.status": "Status",
      "users.active": "Aktiv",
      "users.inactive": "Inaktiv",
      "users.confirmDelete": "Möchten Sie diesen Benutzer wirklich löschen?",
      "users.invitationSent": "Einladungs-E-Mail gesendet",
      "users.userCreated": "Benutzer erfolgreich erstellt",
      "users.userUpdated": "Benutzer erfolgreich aktualisiert",
      "users.userDeleted": "Benutzer erfolgreich gelöscht",

      // Teams Management
      "teams.title": "Teams Verwalten",
      "teams.addTeam": "Team Hinzufügen",
      "teams.editTeam": "Team Bearbeiten",
      "teams.deleteTeam": "Team Löschen",
      "teams.teamName": "Teamname",
      "teams.teamDescription": "Teambeschreibung",
      "teams.supervisor": "Vorgesetzter",
      "teams.selectSupervisor": "Vorgesetzten auswählen",
      "teams.members": "Teammitglieder",
      "teams.addMembers": "Mitglieder Hinzufügen",
      "teams.removeMembers": "Mitglieder Entfernen",
      "teams.memberCount": "{{count}} Mitglieder",
      "teams.noMembers": "Keine Mitglieder zugewiesen",
      "teams.teamCreated": "Team erfolgreich erstellt",
      "teams.teamUpdated": "Team erfolgreich aktualisiert",
      "teams.teamDeleted": "Team erfolgreich gelöscht",
      "teams.confirmDelete": "Möchten Sie dieses Team wirklich löschen?",

      // Billing
      "billing.title": "Abrechnung & Abonnement",
      "billing.subscription": "Abonnementdetails",
      "billing.status": "Status",
      "billing.plan": "Tarif",
      "billing.activeUsers": "Aktive Benutzer",
      "billing.monthlyCost": "Monatliche Kosten",
      "billing.nextBilling": "Nächstes Abrechnungsdatum",
      "billing.paymentMethod": "Zahlungsmethode",
      "billing.updatePayment": "Zahlungsmethode Aktualisieren",
      "billing.viewInvoices": "Rechnungen Ansehen",
      "billing.cancelSubscription": "Abonnement Kündigen",
      "billing.trialEnds": "Testphase endet am {{date}}",
      "billing.perUserPerMonth": "pro Benutzer/Monat",
      "billing.currency": "Währung",
      "billing.setupPayment": "Zahlung Einrichten",

      // 2FA
      "2fa.title": "Zwei-Faktor-Authentifizierung",
      "2fa.enable": "2FA Aktivieren",
      "2fa.disable": "2FA Deaktivieren",
      "2fa.scanQR": "QR-Code Scannen",
      "2fa.enterCode": "Bestätigungscode eingeben",
      "2fa.backupCodes": "Backup-Codes",
      "2fa.saveBackupCodes": "Bewahren Sie diese Backup-Codes sicher auf",
      "2fa.verificationCode": "6-stelliger Code",
      "2fa.verify": "Bestätigen & Aktivieren",
      "2fa.enabled": "2FA ist aktiviert",
      "2fa.disabled": "2FA ist deaktiviert",
      "2fa.required": "2FA wird von Ihrer Firma verlangt",
      "2fa.optional": "2FA ist optional",

      // Common
      "common.save": "Speichern",
      "common.cancel": "Abbrechen",
      "common.delete": "Löschen",
      "common.edit": "Bearbeiten",
      "common.close": "Schließen",
      "common.submit": "Absenden",
      "common.loading": "Wird geladen...",
      "common.success": "Erfolg",
      "common.error": "Fehler",
      "common.confirm": "Bestätigen",
      "common.yes": "Ja",
      "common.no": "Nein",

      // Messages
      "message.success.clockIn": "Erfolgreich eingestempelt!",
      "message.success.clockOut": "Erfolgreich ausgestempelt!",
      "message.error.clockIn": "Einstempeln nicht möglich. Bitte erneut versuchen.",
      "message.error.clockOut": "Ausstempeln nicht möglich. Bitte erneut versuchen.",
      "message.success.jobCreated": "Auftrag erfolgreich erstellt!",
      "message.success.jobUpdated": "Auftrag erfolgreich aktualisiert!",
      "message.success.jobDeleted": "Auftrag erfolgreich gelöscht!",
      "message.success.jobStarted": "Auftrag erfolgreich gestartet!",
      "message.success.jobCompleted": "Auftrag erfolgreich abgeschlossen!",
      "message.success.photoUploaded": "Foto erfolgreich hochgeladen!",
      "message.success.photoDeleted": "Foto erfolgreich gelöscht!",
      "message.success.profileUpdated": "Profil erfolgreich aktualisiert!",
      "message.success.passwordChanged": "Passwort erfolgreich geändert!",
      "message.success.settingsSaved": "Einstellungen erfolgreich gespeichert!",
      "message.error.uploadFailed": "Hochladen fehlgeschlagen. Bitte erneut versuchen.",
      "message.error.locationRequired": "Standortzugriff ist für diese Funktion erforderlich.",
      "message.error.tooFarFromJob": "Sie sind zu weit vom Auftragsort entfernt.",
      "message.error.photosRequired": "Bitte laden Sie die erforderlichen Fotos vor dem Abschluss hoch.",
      "message.error.invalidInput": "Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut.",
      "message.error.serverError": "Serverfehler. Bitte versuchen Sie es später erneut.",
      "message.error.unauthorized": "Sie haben keine Berechtigung für diese Aktion.",
      "message.warning.unsavedChanges": "Sie haben ungespeicherte Änderungen.",
      "message.confirm.delete": "Diese Aktion kann nicht rückgängig gemacht werden.",

      // Map
      "map.title": "Team- & Auftragskarte",
      "map.activeUsers": "Aktive Benutzer",
      "map.jobLocations": "Auftragsstandorte",
      "map.noActiveUsers": "Derzeit keine Benutzer eingestempelt",
      "map.noJobs": "Keine Aufträge mit Standorten",
      "map.clockedInAt": "Eingestempelt um {{time}}",
      "map.lastUpdated": "Zuletzt aktualisiert: {{time}}",
      "map.refreshMap": "Karte Aktualisieren",

      // Company Registration
      "register.title": "Firma Registrieren",
      "register.companyDetails": "Firmendetails",
      "register.adminDetails": "Administrator-Details",
      "register.companyName": "Firmenname",
      "register.streetAddress": "Straße",
      "register.city": "Stadt",
      "register.state": "Bundesland/Region",
      "register.postalCode": "Postleitzahl",
      "register.country": "Land",
      "register.createAccount": "Konto Erstellen",
      "register.alreadyHaveAccount": "Haben Sie bereits ein Konto?",
      "register.signInHere": "Hier anmelden",
      "register.companyRegistered": "Firma erfolgreich registriert!",

      // Time Editor
      "timeEditor.title": "Zeiteintrag-Editor",
      "timeEditor.selectUser": "Benutzer zum Bearbeiten auswählen",
      "timeEditor.selectDate": "Datumsbereich auswählen",
      "timeEditor.fetchEntries": "Einträge Abrufen",
      "timeEditor.noEntries": "Keine Einträge für diesen Zeitraum gefunden",
      "timeEditor.editEntry": "Eintrag Bearbeiten",
      "timeEditor.deleteEntry": "Eintrag Löschen",
      "timeEditor.editReason": "Bearbeitungsgrund",
      "timeEditor.deleteReason": "Löschgrund",
      "timeEditor.reasonRequired": "Grund ist erforderlich",
      "timeEditor.clockInTime": "Einstempelzeit",
      "timeEditor.clockOutTime": "Ausstempelzeit",
      "timeEditor.auditLog": "Prüfprotokoll",
    }
  },
  it: {
    translation: {
      // Navigation
      "nav.home": "Home",
      "nav.jobs": "Lavori",
      "nav.map": "Mappa",
      "nav.reports": "Rapporti",
      "nav.settings": "Impostazioni",
      "nav.signIn": "Accedi",
      "nav.signOut": "Disconnetti",
      "nav.getStarted": "Inizia Gratis",
      "jobs.tab.paused": "In pausa",
      "jobs.tab.invoiced": "Fatturato",
      "jobs.tab.archive": "Archivio",
      "jobs.searchPlaceholder": "Cerca per assegnatario, titolo, cliente…",
      "reports.subtitle2": "Analisi e dati completi",
      "nav.dashboard": "Pannello",
      "nav.myDay": "La mia giornata",
      "nav.jobsBoard": "Bacheca lavori",
      "nav.workplans": "Piani di lavoro",
      "nav.taskBoards": "Bacheche attività",
      "nav.notes": "Note",
      "nav.staff": "Personale",
      "nav.myTeam": "La mia squadra",
      "nav.teamManagement": "Gestione squadre",
      "nav.timeOff": "Ferie",
      "nav.estimates": "Preventivi",
      "nav.customers": "Clienti",
      "nav.projects": "Progetti",
      "nav.billing": "Fatturazione",
      "nav.auditLog": "Registro di controllo",
      "nav.archive": "Archivio",
      "nav.section.work": "Lavoro",
      "nav.section.people": "Persone",
      "nav.section.money": "Finanze",
      "nav.section.admin": "Admin",
      "nav.section.customers": "Clienti",
      "nav.section.pinned": "Fissati",
      "nav.approvals": "Approvazioni",
      "nav.liveMap": "Mappa live",
      "nav.payroll": "Paghe",
      "nav.invoices": "Fatture",
      "nav.onClock": "Al lavoro",
      
      // Landing Page
      "landing.hero.title": "Tracciamento Tempo di Lavoro,",
      "landing.hero.subtitle": "Reso Semplice",
      "landing.hero.description": "Smetti di perdere tempo con i fogli presenze. Traccia ore, gestisci lavori, verifica posizioni e elabora paghe con un'unica potente piattaforma.",
      "landing.hero.startTrial": "Inizia Prova Gratuita di 30 Giorni",
      "landing.hero.watchDemo": "Guarda Demo",
      "landing.hero.requestDemo": "Richiedi Demo",
      "landing.hero.noCard": "30 giorni gratis, poi fatturazione automatica",
      "landing.hero.quickSetup": "Configurazione in 5 minuti",
      
      // Features
      "features.timeTracking": "Tracciamento Tempo Intelligente",
      "features.timeTracking.desc": "Entrata/uscita automatizzata con controlli benessere e GPS",
      "features.location": "Verifica Posizione",
      "features.location.desc": "Assicurati che i lavoratori siano sul posto con tracciamento GPS",
      "features.team": "Gestione Team",
      "features.team.desc": "Organizza team, assegna supervisori e gestisci tutta la forza lavoro",
      "features.reports": "Rapporti Avanzati",
      "features.reports.desc": "Rapporti paghe, analisi attività ed esportazioni CSV",
      "features.jobs": "Gestione Lavori",
      "features.jobs.desc": "Assegna lavori, traccia progressi e richiedi documentazione fotografica",
      "features.security": "Sicuro e Conforme",
      "features.security.desc": "Sicurezza di livello aziendale con piste di controllo complete",
      
      // Auth
      "auth.welcomeBack": "Bentornato",
      "auth.signInToTrack": "Accedi per tracciare il tuo tempo",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.signIn": "Accedi",
      "auth.forgotPassword": "Password Dimenticata?",
      "auth.newToHinesTime": "Nuovo su Hines Time?",
      "auth.registerCompany": "Registra la Tua Azienda",
      
      // Home/Dashboard
      "home.goodMorning": "Buongiorno",
      "home.goodAfternoon": "Buon Pomeriggio",
      "home.goodEvening": "Buonasera",
      "home.clockIn": "Timbra Entrata",
      "home.clockOut": "Timbra Uscita",
      "home.clockedIn": "TIMBRATO",
      "home.readyToClock": "PRONTO A TIMBRARE",
      "home.todayHours": "Ore di Oggi",
      "home.weekHours": "Ore della Settimana",
      "home.recentActivity": "Attività Recente",
      "home.yourJobs": "I Tuoi Lavori",
      
      // Supervisor Team Management
      "supervisor.teamManagement": "Gestione Team",
      "supervisor.clockInTeam": "Timbra Team Entrata",
      "supervisor.clockOutTeam": "Timbra Team Uscita",
      "supervisor.teamDescription": "Come supervisore, puoi timbrare entrata o uscita dell'intero team con una singola azione.",
      "supervisor.clockInResults": "Risultati Timbratura Team:",
      "supervisor.clockOutResults": "Risultati Timbratura Uscita Team:",
      "supervisor.clockedIn": "Timbrati",
      "supervisor.clockedOut": "Timbrati Uscita",
      "supervisor.alreadyIn": "Già Timbrati",
      "supervisor.notClockedIn": "Non Timbrati",
      "supervisor.totalMembers": "Membri Totali",
      
      // Wellness
      "wellness.title": "Verifica Benessere Richiesta",
      "wellness.subtitle": "Sicurezza Prima!",
      "wellness.message": "Prima di timbrare, conferma di essere fisicamente e mentalmente pronto per il lavoro.",
      "wellness.confirm": "Confermo di essere idoneo al lavoro e pronto a lavorare in sicurezza",
      "wellness.confirmButton": "✓ Conferma e Timbra Entrata",
      "wellness.cancel": "Annulla",
      
      // Toolbox
      "toolbox.title": "Riunione Cassetta Attrezzi",
      "toolbox.readTopic": "Leggi l'argomento di sicurezza di oggi prima di iniziare a lavorare",
      "toolbox.openPdf": "Apri Documento PDF di Sicurezza",
      "toolbox.iReadThis": "Ho Letto Questo - Continua a Timbrare",
      "toolbox.goBack": "Indietro",
      "toolbox.question": "Controllo Rapido di Sicurezza",
      "toolbox.didYouRead": "Hai letto l'argomento della cassetta attrezzi di oggi?",
      "toolbox.yesIRead": "Sì, L'ho Letto",
      "toolbox.noIDidnt": "No, Non L'ho Fatto",
      "toolbox.compliance": "Conformità Riunioni Cassetta Attrezzi",
      
      // Jobs - Three-Tab System
      "jobs.title": "Lavori",
      "jobs.addJob": "Aggiungi Lavoro",
      "jobs.startJob": "Inizia Lavoro",
      "jobs.completeJob": "Completa Lavoro",
      "jobs.addPhotos": "Aggiungi Foto",
      "jobs.viewPhotos": "Visualizza Foto",
      "jobs.photosRequired": "Foto Richieste",
      "jobs.assignedTo": "Assegnato a",
      "jobs.customer": "Cliente",
      "jobs.location": "Posizione",
      "jobs.jobTitle": "Titolo del Lavoro",
      "jobs.jobDescription": "Descrizione del Lavoro",
      "jobs.enterTitle": "Inserisci il titolo del lavoro",
      "jobs.enterDescription": "Inserisci la descrizione del lavoro",
      "jobs.selectUser": "Seleziona utente",
      "jobs.customerName": "Nome del cliente (opzionale)",
      "jobs.customerEmail": "Email del cliente (opzionale)",
      "jobs.streetAddress": "Indirizzo",
      "jobs.city": "Città",
      "jobs.state": "Stato/Provincia",
      "jobs.postalCode": "Codice Postale",
      "jobs.country": "Paese",
      "jobs.selectCountry": "Seleziona paese",
      "jobs.selectState": "Seleziona stato",
      "jobs.createJob": "Crea Lavoro",
      "jobs.cancel": "Annulla",
      "jobs.editJob": "Modifica Lavoro",
      "jobs.deleteJob": "Elimina Lavoro",
      "jobs.reassignJob": "Riassegna Lavoro",
      "jobs.sendReminder": "Invia Promemoria",
      "jobs.acceptJob": "Accetta Lavoro",
      "jobs.rejectJob": "Rifiuta Lavoro",
      "jobs.viewOnMap": "Visualizza sulla Mappa",
      "jobs.uploadPhotos": "Carica Foto",
      "jobs.emailPhotos": "Invia Foto via Email",
      "jobs.emailReport": "Invia Rapporto via Email",
      "jobs.viewStats": "Visualizza Statistiche",
      "jobs.tab.todo": "Da Fare",
      "jobs.tab.inProgress": "In Corso",
      "jobs.tab.completed": "Completati",
      "jobs.status.pending": "In Attesa",
      "jobs.status.accepted": "Accettato",
      "jobs.status.rejected": "Rifiutato",
      "jobs.status.inProgress": "In Corso",
      "jobs.status.completed": "Completato",
      "jobs.overdue": "SCADUTO",
      "jobs.duration": "Durata: {{duration}}",
      "jobs.completedBy": "Completato da {{name}}",
      "jobs.noJobs": "Nessun lavoro trovato",
      "jobs.noTodoJobs": "Nessun lavoro in attesa",
      "jobs.noInProgressJobs": "Nessun lavoro in corso",
      "jobs.noCompletedJobs": "Nessun lavoro completato",
      
      // Reports
      "reports.title": "Rapporti e Analisi",
      "reports.subtitle": "Informazioni e dati completi",
      "reports.supervisorView": "Vista Supervisore - Rapporti Specifici del Team",
      "reports.supervisorDescription": "Stai visualizzando rapporti filtrati solo per i membri del tuo team. I dati mostrati di seguito includono solo attività, ore e lavori relativi al tuo team assegnato.",
      "reports.yourTeam": "Il Tuo Team:",
      "reports.noTeamAssigned": "Attualmente non sei assegnato a nessun team. Si prega di contattare l'amministratore.",
      "reports.refresh": "Aggiorna",
      "reports.generateReports": "Genera Rapporti",
      "reports.startDate": "Data di Inizio",
      "reports.endDate": "Data di Fine",
      
      // Settings
      "settings.title": "Impostazioni",
      "settings.profile": "Informazioni Profilo",
      "settings.editProfile": "Modifica Profilo",
      "settings.changePassword": "Cambia Password",
      "settings.currentPassword": "Password Attuale",
      "settings.newPassword": "Nuova Password",
      "settings.confirmPassword": "Conferma Password",
      "settings.management": "Gestione",
      "settings.manageUsers": "Gestisci Utenti",
      "settings.manageTeams": "Gestisci Team",
      "settings.toolboxTopics": "Argomenti Riunione Cassetta Attrezzi",
      "settings.billing": "Fatturazione e Abbonamento",
      "settings.companyBranding": "Branding Aziendale",
      "settings.twoFactor": "Autenticazione a Due Fattori",
      "settings.userManual": "Manuale Utente",
      "settings.termsOfService": "Termini di Servizio",
      "settings.privacyPolicy": "Politica sulla Privacy",
      
      // Users Management
      "users.title": "Gestisci Utenti",
      "users.addUser": "Aggiungi Utente",
      "users.editUser": "Modifica Utente",
      "users.deleteUser": "Elimina Utente",
      "users.resendInvitation": "Reinvia Invito",
      "users.firstName": "Nome",
      "users.lastName": "Cognome",
      "users.email": "Indirizzo Email",
      "users.phone": "Numero di Telefono",
      "users.role": "Ruolo",
      "users.userType": "Tipo di Utente",
      "users.role.admin": "Amministratore",
      "users.role.supervisor": "Supervisore",
      "users.role.user": "Utente",
      "users.type.employee": "Dipendente",
      "users.type.contractor": "Appaltatore",
      "users.confirmDelete": "Sei sicuro di voler eliminare questo utente?",
      
      // Teams Management
      "teams.title": "Gestisci Team",
      "teams.addTeam": "Aggiungi Team",
      "teams.editTeam": "Modifica Team",
      "teams.deleteTeam": "Elimina Team",
      "teams.teamName": "Nome del Team",
      "teams.supervisor": "Supervisore",
      "teams.members": "Membri del Team",
      "teams.confirmDelete": "Sei sicuro di voler eliminare questo team?",
      
      // Common
      "common.save": "Salva",
      "common.cancel": "Annulla",
      "common.delete": "Elimina",
      "common.edit": "Modifica",
      "common.close": "Chiudi",
      "common.submit": "Invia",
      "common.loading": "Caricamento...",
      "common.success": "Successo",
      "common.error": "Errore",
      "common.confirm": "Conferma",
      "common.yes": "Sì",
      "common.no": "No",
      
      // Messages
      "message.success.clockIn": "Entrata timbrata con successo!",
      "message.success.clockOut": "Uscita timbrata con successo!",
      "message.error.clockIn": "Impossibile timbrare. Riprova.",
      "message.error.clockOut": "Impossibile timbrare uscita. Riprova.",
      "message.success.jobCreated": "Lavoro creato con successo!",
      "message.success.jobUpdated": "Lavoro aggiornato con successo!",
      "message.success.jobDeleted": "Lavoro eliminato con successo!",
      "message.success.jobStarted": "Lavoro iniziato con successo!",
      "message.success.jobCompleted": "Lavoro completato con successo!",
      "message.success.photoUploaded": "Foto caricata con successo!",
      "message.success.photoDeleted": "Foto eliminata con successo!",
      "message.success.profileUpdated": "Profilo aggiornato con successo!",
      "message.success.passwordChanged": "Password cambiata con successo!",
      "message.success.settingsSaved": "Impostazioni salvate con successo!",
      "message.error.uploadFailed": "Caricamento fallito. Riprova.",
      "message.error.locationRequired": "L'accesso alla posizione è richiesto per questa funzione.",
      "message.error.tooFarFromJob": "Sei troppo lontano dalla posizione del lavoro.",
      "message.error.photosRequired": "Carica le foto richieste prima di completare.",
      "message.error.invalidInput": "Verifica il tuo input e riprova.",
      "message.error.serverError": "Errore del server. Riprova più tardi.",
      "message.error.unauthorized": "Non hai il permesso di eseguire questa azione.",
      "message.warning.unsavedChanges": "Hai modifiche non salvate.",
      "message.confirm.delete": "Questa azione non può essere annullata.",
      
      // Jobs - Additional keys
      "jobs.gpsCoordinates": "Coordinate GPS (Opzionale)",
      "jobs.latitude": "Latitudine",
      "jobs.longitude": "Longitudine",
      "jobs.requirePhotos": "Richiedi foto di completamento lavoro",
      "jobs.requireLocation": "Richiedi verifica della posizione",
      "jobs.locationHelp": "Il lavoratore deve essere entro 500m dal sito per iniziare",
      "jobs.noLocationHelp": "Il lavoratore può iniziare il lavoro da qualsiasi luogo",
      "jobs.assignedAgo": "Assegnato {{time}} fa",
      "jobs.startedAt": "Iniziato alle {{time}}",
      "jobs.completedAt": "Completato alle {{time}}",
      "jobs.photos": "{{count}} foto",
      "jobs.noPhotos": "Nessuna foto",
      "jobs.photosCount": "{{count}}/{{total}} foto",
      "jobs.morePhotos": "+{{count}} altre",
      "jobs.confirmDelete": "Sei sicuro di voler eliminare questo lavoro?",
      "jobs.confirmReject": "Sei sicuro di voler rifiutare questo lavoro?",
      "jobs.rejectReason": "Motivo del rifiuto (opzionale)",
      "jobs.completionNotes": "Note di Completamento",
      "jobs.enterNotes": "Inserisci note di completamento...",
      "jobs.loading": "Caricamento lavori...",
      
      // Reports - Additional keys
      "reports.payroll": "Rapporto Paghe",
      "reports.userActivity": "Attività Utenti",
      "reports.jobStats": "Statistiche Lavori",
      "reports.teamPerformance": "Performance del Team",
      "reports.timeEditor": "Editor Tempo",
      "reports.toolboxCompliance": "Conformità Cassetta Attrezzi",
      "reports.deletedEntries": "Voci Eliminate",
      "reports.totalHours": "Ore Totali",
      "reports.totalEntries": "Voci Totali",
      "reports.activeUsers": "Utenti Attivi",
      "reports.exportCSV": "Esporta in CSV",
      "reports.noData": "Nessun dato disponibile per questo periodo",
      "reports.userName": "Nome Utente",
      "reports.hours": "Ore",
      "reports.clockIns": "Timbrature",
      "reports.avgHoursPerDay": "Ore Med/Giorno",
      "reports.jobsCompleted": "Lavori Completati",
      "reports.teamName": "Nome Team",
      "reports.memberCount": "Membri",
      "reports.avgHoursPerMember": "Ore Med/Membro",
      "reports.completionRate": "Tasso di Completamento",
      "reports.statusBreakdown": "Ripartizione per Stato",
      "reports.complianceRate": "Tasso di Conformità",
      "reports.topicDate": "Data Argomento",
      "reports.topicTitle": "Titolo Argomento",
      "reports.acknowledged": "Riconosciuto",
      "reports.notAcknowledged": "Non Riconosciuto",
      "reports.editEntry": "Modifica Voce",
      "reports.deleteEntry": "Elimina Voce",
      "reports.date": "Data",
      "reports.clockIn": "Timbratura",
      "reports.clockOut": "Timbratura Uscita",
      "reports.editedBy": "Modificato Da",
      "reports.editedAt": "Modificato Il",
      "reports.reason": "Motivo",
      "reports.originalTime": "Tempo Originale",
      "reports.newTime": "Nuovo Tempo",
      "reports.auditTrail": "Traccia di Controllo",
      
      // Settings - Additional keys
      "settings.firstName": "Nome",
      "settings.lastName": "Cognome",
      "settings.email": "Email",
      "settings.phone": "Telefono",
      "settings.address": "Indirizzo",
      "settings.role": "Ruolo",
      "settings.updateProfile": "Aggiorna Profilo",
      "settings.uploadLogo": "Carica Logo",
      "settings.companyName": "Nome Azienda",
      "settings.companyInfo": "Informazioni Azienda",
      "settings.notifications": "Notifiche Email",
      "settings.notifyOnJobComplete": "Notifica gli admin quando i lavori sono completati",
      "settings.notifyCustomer": "Invia automaticamente email ai clienti al completamento lavoro",
      
      // Users Management - Additional keys
      "users.selectRole": "Seleziona ruolo",
      "users.selectType": "Seleziona tipo",
      "users.status": "Stato",
      "users.active": "Attivo",
      "users.inactive": "Inattivo",
      "users.invitationSent": "Email di invito inviata",
      "users.userCreated": "Utente creato con successo",
      "users.userUpdated": "Utente aggiornato con successo",
      "users.userDeleted": "Utente eliminato con successo",
      
      // Teams Management - Additional keys
      "teams.teamDescription": "Descrizione del Team",
      "teams.selectSupervisor": "Seleziona supervisore",
      "teams.addMembers": "Aggiungi Membri",
      "teams.removeMembers": "Rimuovi Membri",
      "teams.memberCount": "{{count}} membri",
      "teams.noMembers": "Nessun membro assegnato",
      "teams.teamCreated": "Team creato con successo",
      "teams.teamUpdated": "Team aggiornato con successo",
      "teams.teamDeleted": "Team eliminato con successo",
      
      // Billing & Payments
      "billing.title": "Fatturazione e Abbonamento",
      "billing.subscription": "Dettagli Abbonamento",
      "billing.status": "Stato",
      "billing.plan": "Piano",
      "billing.activeUsers": "Utenti Attivi",
      "billing.monthlyCost": "Costo Mensile",
      "billing.nextBilling": "Prossima Data di Fatturazione",
      "billing.setupFee": "Tariffa di Installazione",
      "billing.setupFeePaid": "Pagato",
      "billing.setupFeePending": "Pagamento Richiesto",
      "billing.paymentMethod": "Metodo di Pagamento",
      "billing.updatePayment": "Aggiorna Metodo di Pagamento",
      "billing.viewInvoices": "Visualizza Fatture",
      "billing.cancelSubscription": "Annulla Abbonamento",
      "billing.trialEnds": "La prova termina il {{date}}",
      "billing.perUserPerMonth": "per utente/mese",
      "billing.currency": "Valuta",
      "billing.setupPayment": "Configura Pagamento",
      
      // Two-Factor Authentication
      "2fa.title": "Autenticazione a Due Fattori",
      "2fa.enable": "Attiva 2FA",
      "2fa.disable": "Disattiva 2FA",
      "2fa.scanQR": "Scansiona Codice QR",
      "2fa.enterCode": "Inserisci codice di verifica",
      "2fa.backupCodes": "Codici di Backup",
      "2fa.saveBackupCodes": "Salva questi codici di backup in un luogo sicuro",
      "2fa.verificationCode": "Codice a 6 cifre",
      "2fa.verify": "Verifica e Attiva",
      "2fa.enabled": "2FA è attivato",
      "2fa.disabled": "2FA è disattivato",
      "2fa.required": "2FA è richiesto dalla tua azienda",
      "2fa.optional": "2FA è opzionale",
      
      // Auth & Password - Additional keys
      "auth.enterEmail": "Inserisci la tua email",
      "auth.enterPassword": "Inserisci la tua password",
      "auth.resetPassword": "Reimposta Password",
      "auth.sendResetLink": "Invia Link di Reimpostazione",
      "auth.backToSignIn": "Torna ad Accedi",
      "auth.resetLinkSent": "Link di reimpostazione inviato alla tua email",
      "auth.createNewPassword": "Crea Nuova Password",
      "auth.passwordRequirements": "La password deve contenere almeno 8 caratteri",
      "auth.passwordMismatch": "Le password non corrispondono",
      "auth.invalidCredentials": "Email o password non validi",
      "auth.accountLocked": "L'account è bloccato. Contatta l'amministratore.",
      "auth.sessionExpired": "Sessione scaduta. Effettua nuovamente l'accesso.",
      "auth.firstLoginRequired": "Cambia la password al primo accesso",
      "auth.selectOrganization": "Seleziona Organizzazione",
      "auth.multipleOrganizations": "La tua email è registrata con più organizzazioni",
      
      // Map
      "map.title": "Mappa Team e Lavori",
      "map.activeUsers": "Utenti Attivi",
      "map.jobLocations": "Posizioni Lavori",
      "map.noActiveUsers": "Nessun utente attualmente timbrato",
      "map.noJobs": "Nessun lavoro con posizioni",
      "map.clockedInAt": "Timbrato alle {{time}}",
      "map.lastUpdated": "Ultimo aggiornamento: {{time}}",
      "map.refreshMap": "Aggiorna Mappa",
      "map.centerOnUser": "Centra sulla mia posizione",
      "map.showAllUsers": "Mostra tutti gli utenti",
      "map.showAllJobs": "Mostra tutti i lavori",
      "map.filterByTeam": "Filtra per team",
      
      // Company Registration
      "register.title": "Registra la Tua Azienda",
      "register.companyDetails": "Dettagli Azienda",
      "register.adminDetails": "Dettagli Amministratore",
      "register.companyName": "Nome Azienda",
      "register.streetAddress": "Indirizzo",
      "register.city": "Città",
      "register.state": "Stato/Provincia",
      "register.postalCode": "Codice Postale",
      "register.country": "Paese",
      "register.adminFirstName": "Nome Admin",
      "register.adminLastName": "Cognome Admin",
      "register.adminEmail": "Email Admin",
      "register.adminPhone": "Telefono Admin",
      "register.adminPassword": "Password Admin",
      "register.confirmPassword": "Conferma Password",
      "register.agreeToTerms": "Accetto i Termini di Servizio e la Politica sulla Privacy",
      "register.createAccount": "Crea Account",
      "register.alreadyHaveAccount": "Hai già un account?",
      "register.signInHere": "Accedi qui",
      "register.companyRegistered": "Azienda registrata con successo!",
      
      // Time Editor
      "timeEditor.title": "Editor Voce Tempo",
      "timeEditor.selectUser": "Seleziona utente da modificare",
      "timeEditor.selectDate": "Seleziona intervallo di date",
      "timeEditor.fetchEntries": "Recupera Voci",
      "timeEditor.noEntries": "Nessuna voce trovata per questo periodo",
      "timeEditor.editEntry": "Modifica Voce",
      "timeEditor.bulkEdit": "Modifica di Massa",
      "timeEditor.deleteEntry": "Elimina Voce",
      "timeEditor.editReason": "Motivo della Modifica",
      "timeEditor.deleteReason": "Motivo dell'Eliminazione",
      "timeEditor.reasonRequired": "Il motivo è richiesto",
      "timeEditor.clockInTime": "Ora Timbratura",
      "timeEditor.clockOutTime": "Ora Timbratura Uscita",
      "timeEditor.originalHours": "Originale: {{hours}}",
      "timeEditor.newHours": "Nuovo: {{hours}}",
      "timeEditor.auditLog": "Registro di Controllo",
      "timeEditor.editedBy": "Modificato da {{name}} il {{date}}",
      "timeEditor.deletedBy": "Eliminato da {{name}} il {{date}}",
      "timeEditor.applyShift": "Applica Durata Turno",
      "timeEditor.shiftDuration": "Durata Turno (ore)",
      "timeEditor.applyToAll": "Applica a tutte le voci selezionate",
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'es', 'de', 'it'],
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
